import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';
import StockOrderingView from '../components/StockOrderingView';
import StocktakeView from '../components/StocktakeView';
import StockEfficiencyView from '../components/StockEfficiencyView';
import AppHeader from '../components/AppHeader';
import { 
  Upload, 
  Package, 
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  Save,
  X,
  Edit,
  Calendar,
  Truck,
  ShoppingCart,
  BarChart3,
  ClipboardList,
  Filter,
  Eye,
  Home,
  TrendingUp,
  TrendingDown,
  Target,
  Loader2,
  ArrowUpDown,
  Bell,
  Check,
  RefreshCw,
  Mail,
  Copy,
  CheckCircle2,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Safe date formatting helper
const safeFormatDate = (dateValue, formatStr, fallback = '-') => {
  if (!dateValue) return fallback;
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return fallback;
    return format(date, formatStr);
  } catch (e) {
    return fallback;
  }
};

// ── Free Stock Report Banner ──────────────────────────────────────────────
const DAILY_STOCK_PATH = 'N:\\Business Intelligence\\30 – Birmingham\\30 - Daily - Stock Profile';
const DAILY_STOCK_PATH_PARENT = 'N:\\Business Intelligence\\30 – Birmingham';

function UploadReportsBar({ needFreeStock, needDailyProfile, onFreeStockUploaded, onDailyProfileUploaded }) {
  const [uploadingFree, setUploadingFree] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPath, setCopiedPath] = useState(false);
  const freeFileRef = useRef(null);
  const profileFileRef = useRef(null);

  const copyEmail = () => {
    navigator.clipboard.writeText('orders@c30dashboard.uk').then(() => {
      setCopiedEmail(true);
      toast.success('Email address copied to clipboard');
      setTimeout(() => setCopiedEmail(false), 3000);
    });
  };
  const copyPath = () => {
    navigator.clipboard.writeText(DAILY_STOCK_PATH).then(() => {
      setCopiedPath(true);
      toast.success('Path copied — paste it into the file picker address bar');
      setTimeout(() => setCopiedPath(false), 4000);
    });
  };

  const handleFreeStockFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a .csv or Excel (.xlsx/.xls) file');
      return;
    }
    setUploadingFree(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`${API}/pi-counts/upload-stock`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Free Stock Report uploaded successfully');
      onFreeStockUploaded?.();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || 'unknown error'));
    } finally {
      setUploadingFree(false);
    }
  };

  const handleDailyProfileFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload an Excel (.xlsx or .xls) file');
      return;
    }
    setUploadingProfile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`${API}/stock-ordering/daily/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Daily Stock Profile uploaded successfully!');
      onDailyProfileUploaded?.();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || 'unknown error'));
    } finally {
      setUploadingProfile(false);
    }
  };

  if (!needFreeStock && !needDailyProfile) return null;

  return (
    <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3" data-testid="upload-reports-bar">
      <p className="text-sm font-semibold text-red-700 mb-2.5 flex items-center gap-1.5">
        <AlertCircle className="w-4 h-4" /> Upload Reports
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {needFreeStock && (
          <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white/70 rounded-lg border border-red-100 px-3 py-2.5" data-testid="upload-reports-free-stock-box">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-red-700">Free Stock Report</p>
              <p className="text-[11px] text-red-500 mt-0.5">
                Upload below, or send from M4 to{' '}
                <button onClick={copyEmail} className="inline-flex items-center gap-1 font-mono font-semibold text-red-600 hover:text-red-800 underline underline-offset-2 transition-colors" title="Click to copy email address" data-testid="copy-email-btn">
                  orders@c30dashboard.uk {copiedEmail ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              </p>
            </div>
            <input ref={freeFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => handleFreeStockFile(e.target.files[0])} />
            <button
              onClick={() => freeFileRef.current?.click()}
              disabled={uploadingFree}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 flex-shrink-0"
              data-testid="free-stock-upload-btn"
            >
              {uploadingFree ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingFree ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        )}
        {needDailyProfile && (
          <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white/70 rounded-lg border border-red-100 px-3 py-2.5" data-testid="upload-reports-daily-profile-box">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-red-700">Daily Stock Profile (BI report)</p>
              <p className="text-[11px] text-red-500 mt-0.5">
                From{' '}
                <button onClick={copyPath} className="inline-flex items-center gap-1 font-mono font-semibold text-red-600 hover:text-red-800 underline underline-offset-2 transition-colors" title="Click to copy network path" data-testid="copy-daily-path-btn">
                  30 - Daily - Stock Profile {copiedPath ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              </p>
            </div>
            <input ref={profileFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleDailyProfileFile(e.target.files[0])} />
            <button
              onClick={() => profileFileRef.current?.click()}
              disabled={uploadingProfile}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 flex-shrink-0"
              data-testid="daily-profile-upload-btn"
            >
              {uploadingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingProfile ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main StockManagement component ────────────────────────────────────────
const StockManagement = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();
  
  // Color constants
  const enconOrange = '#ee7203';
  
  // Page state: 'stock-ordering', 'daily-overstock', 'weekly-stock', 'stocktake'
  const VALID_STOCK_VIEWS = ['stock-ordering', 'stocktake', 'daily-overstock', 'weekly-stock', 'stock-efficiency'];
  const [currentView, setCurrentView] = useState(
    VALID_STOCK_VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'stock-ordering'
  );
  const [menuDropdownOpen, setMenuDropdownOpen] = useState(false);
  const menuDropdownRef = React.useRef(null);
  const [showEfficiencyReport, setShowEfficiencyReport] = useState(false);
  const [hideSalesBar, setHideSalesBar] = useState(false);
  // Stock Efficiency "change date" pill — lets a user retrace the comparison baseline
  // back a few days (e.g. Thursday/Friday's report) instead of always just yesterday's.
  const [efficiencyOldDate, setEfficiencyOldDate] = useState(null);
  const [efficiencyDatesOpen, setEfficiencyDatesOpen] = useState(false);
  const [efficiencyAvailableDates, setEfficiencyAvailableDates] = useState([]);
  const efficiencyDateBtnRef = useRef(null);
  const efficiencyDatePopoverRef = useRef(null);

  useEffect(() => {
    if (currentView !== 'stock-efficiency') return;
    api.get(`${API}/stock-ordering/efficiency/upload-dates`, { params: { branch_id: effectiveBranchId } })
      .then(res => setEfficiencyAvailableDates(res.data.dates || []))
      .catch(() => {});
  }, [currentView, effectiveBranchId]);

  // Manual click-outside close (matches the existing reminder date-picker pattern in this
  // file) rather than a Radix Popover — this pill lives inside the fixed/z-50 AppHeader,
  // and a plain relative-wrapper + absolute-dropdown is the most reliable approach there.
  useEffect(() => {
    if (!efficiencyDatesOpen) return;
    const handleClickOutside = (e) => {
      if (
        efficiencyDatePopoverRef.current && !efficiencyDatePopoverRef.current.contains(e.target) &&
        efficiencyDateBtnRef.current && !efficiencyDateBtnRef.current.contains(e.target)
      ) {
        setEfficiencyDatesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [efficiencyDatesOpen]);

  // ── Stock Take session state (for orange-bar pill) ─────────────────────────
  const [stockTakes, setStockTakes] = useState([]);
  const [activeStockTakeId, setActiveStockTakeId] = useState(null);
  const [stockTakeDropdownOpen, setStockTakeDropdownOpen] = useState(false);
  const [showNewStocktakeDialog, setShowNewStocktakeDialog] = useState(false);
  const [newStocktakeDate, setNewStocktakeDate] = useState('');
  const [newStocktakeStep, setNewStocktakeStep] = useState(1); // 1=date, 2=upload
  const [creatingST, setCreatingST] = useState(false);
  const [stDragOver, setStDragOver] = useState(false);
  const stFileInputRef = React.useRef(null);
  const stockTakeDropdownRef = React.useRef(null);

  const loadStockTakes = React.useCallback(async () => {
    try {
      const r = await api.get(`${API}/stock-takes`);
      setStockTakes(r.data.stock_takes || []);
      // Auto-select the most recent if none selected
      if (!activeStockTakeId && r.data.stock_takes?.length) {
        setActiveStockTakeId(r.data.stock_takes[0].id);
      }
    } catch { /* silent */ }
  }, [activeStockTakeId]);

  useEffect(() => {
    if (currentView === 'stocktake') loadStockTakes();
  }, [currentView]); // eslint-disable-line

  const activeST = stockTakes.find(st => st.id === activeStockTakeId);

  const handleStocktakeFileSelected = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    setCreatingST(true);
    try {
      // 1. Upload the free stock report — response includes products directly
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await api.post(`${API}/pi-counts/upload-stock`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // 2. Use products returned directly from upload response
      const products = uploadRes.data?.products || [];

      // 3. Create stock take
      const d = new Date(newStocktakeDate);
      const name = `Stocktake ${d.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}`;
      const r = await api.post(`${API}/stock-takes`, { name, date: newStocktakeDate, products });
      const newST = r.data.stock_take;
      setStockTakes(prev => [newST, ...prev]);
      setActiveStockTakeId(newST.id);
      setShowNewStocktakeDialog(false);
      setNewStocktakeDate('');
      setNewStocktakeStep(1);
      toast.success(`${name} created with ${products.length} products`);
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setCreatingST(false);
    }
  };

  const closeNewStocktakeDialog = () => {
    setShowNewStocktakeDialog(false);
    setNewStocktakeDate('');
    setNewStocktakeStep(1);
    setStDragOver(false);
  };

  // Deep-link: ?requestUser=<email> — passed through to StockOrderingView
  const deepLinkRequestUser = searchParams.get('requestUser') || null;

  // SOS toolbar bridge — StockOrderingView exposes these via callback props
  const [sosLatestUploadDate, setSosLatestUploadDate] = useState(null);  // raw ISO upload date
  const [sosNoDaily, setSosNoDaily] = useState(false);
  const [sosUploading, setSosUploading] = useState(false);
  const sosRefreshRef = React.useRef(null);

  // ── Free Stock Report status ───────────────────────────────────────────
  const [freeStockUploadedToday, setFreeStockUploadedToday] = useState(null); // null=loading, bool
  const [freeStockLatest, setFreeStockLatest] = useState(null);
  const profileModalFileRef = React.useRef(null);
  const [isDraggingProfile, setIsDraggingProfile] = useState(false);
  const [uploadingProfileModal, setUploadingProfileModal] = useState(false);

  const loadFreeStockStatus = React.useCallback(async () => {
    try {
      const r = await api.get(`${API}/stock-management/daily-report-status`);
      setFreeStockUploadedToday(r.data.free_stock_report_uploaded_today);
      setFreeStockLatest(r.data.free_stock_report_latest);
    } catch { setFreeStockUploadedToday(false); }
  }, []);

  useEffect(() => { loadFreeStockStatus(); }, [loadFreeStockStatus]);

  // Load settings for header height offset
  useEffect(() => {
    api.get(`${API}/settings`).then(r => {
      setHideSalesBar(r.data?.hide_sales_info_bar || false);
    }).catch(() => {});
  }, []);
  
  // Summary data for home page
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  
  // ================== DAILY OVERSTOCK STATE ==================
  const [dailyQueries, setDailyQueries] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [showDailyUploadModal, setShowDailyUploadModal] = useState(false);
  const [showDailyReviewModal, setShowDailyReviewModal] = useState(false);
  const [showDailySummaryModal, setShowDailySummaryModal] = useState(false);
  const [selectedDailyQuery, setSelectedDailyQuery] = useState(null);
  const [dailyQueryItems, setDailyQueryItems] = useState([]);
  const [dailyUploadedData, setDailyUploadedData] = useState([]);
  const [currentDailyQueryId, setCurrentDailyQueryId] = useState(null);
  const [dailySummaryData, setDailySummaryData] = useState(null);
  const [uploadingDaily, setUploadingDaily] = useState(false);
  const [isDraggingDaily, setIsDraggingDaily] = useState(false);
  const dailyFileInputRef = useRef(null);
  // Complete Report state
  const [showCompleteReportModal, setShowCompleteReportModal] = useState(false);
  const [completeReportData, setCompleteReportData] = useState(null);
  const [completeReportMonths, setCompleteReportMonths] = useState([]);
  const [selectedReportMonths, setSelectedReportMonths] = useState([]);
  const [selectedReportWeeks, setSelectedReportWeeks] = useState([]);
  const [loadingCompleteReport, setLoadingCompleteReport] = useState(false);
  
  // ================== WEEKLY STOCK STATE ==================
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [loadingWeekly, setLoadingWeekly] = useState(true);
  const [selectedWeeklyReport, setSelectedWeeklyReport] = useState(null);
  const [weeklyItems, setWeeklyItems] = useState([]);
  const [allWeeklyItems, setAllWeeklyItems] = useState([]); // Store all items for client-side filtering
  const [weeklySearchQuery, setWeeklySearchQuery] = useState('');
  // Multi-select filters
  const [activeFilters, setActiveFilters] = useState({
    value_above: false,
    no_sales: false,
    fifty_plus_days: false,
    fifty_one_plus_days: false, // 51+ days filter
    product_code: false,
    has_notes: false,
    no_notes: false,
    has_reminder: false, // Items with reminders
    status_critical: false,
    status_monitor: false, // Was "concern" - renamed to "Need to Monitor"
    status_ok: false, // OK - Natural reduction/no concern (Orange)
    status_resolved: false, // NEW: Resolved - Confident will be reduced (Green)
    new_lines: false,  // New entries compared to previous week
    sort_by_value: false // Sort by highest value
  });
  
  // PDF Download Progress
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [previousWeekItems, setPreviousWeekItems] = useState([]); // For new lines comparison
  const [filterValueAbove, setFilterValueAbove] = useState('');
  const [filterProductCode, setFilterProductCode] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  // Pagination
  const [tablePage, setTablePage] = useState(1);
  const itemsPerPage = 100;
  const [showWeeklyUploadModal, setShowWeeklyUploadModal] = useState(false);
  const [showWeeklySummaryModal, setShowWeeklySummaryModal] = useState(false);
  const [weeklySummaryData, setWeeklySummaryData] = useState(null);
  const [weeklyVarianceData, setWeeklyVarianceData] = useState(null);
  const [uploadingWeekly, setUploadingWeekly] = useState(false);
  const [isDraggingWeekly, setIsDraggingWeekly] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showWeekDropdown, setShowWeekDropdown] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [masterStockData, setMasterStockData] = useState([]); // For product group search
  const [manualSupplierGroups, setManualSupplierGroups] = useState([]); // Supplier groups for search
  const weeklyFileInputRef = useRef(null);
  const monthDropdownRef = useRef(null);
  const weekDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  
  // ================== WEEKLY STOCK REMINDERS STATE ==================
  const [weeklyReminders, setWeeklyReminders] = useState([]);
  const [todayReminders, setTodayReminders] = useState([]);
  const [previousReminders, setPreviousReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [showPreviousReminders, setShowPreviousReminders] = useState(false);
  const [showUpcomingReminders, setShowUpcomingReminders] = useState(false);
  const [showTodayReminders, setShowTodayReminders] = useState(false);
  const [activeReminderItem, setActiveReminderItem] = useState(null); // Item ID with open reminder form
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('08:00');
  const [reminderNote, setReminderNote] = useState('');
  const [filterByReminder, setFilterByReminder] = useState(null); // Reminder to filter by
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const dateButtonRef = useRef(null);
  const timeButtonRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) {
        setShowMonthDropdown(false);
      }
      if (weekDropdownRef.current && !weekDropdownRef.current.contains(e.target)) {
        setShowWeekDropdown(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load master stock data for product group searches
  const loadMasterStockData = useCallback(async () => {
    try {
      const response = await api.get(`${API}/stock-database/search-data`);
      const data = response.data || {};
      setMasterStockData(data.products || []);
      
      // Also load manual supplier groups for better search matching
      const groupsResponse = await api.get(`${API}/stock-database/manual-supplier-groups`);
      setManualSupplierGroups(groupsResponse.data.groups || []);
    } catch (error) {
      console.error('Error loading master stock:', error);
      // Fallback - search will just use direct item matching
    }
  }, []);
  
  // Load summary data for home page
  const loadSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const response = await api.get(`${API}/stock-management/summary`, {
        params: { branch_id: effectiveBranchId }
      });
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  }, [effectiveBranchId]);
  
  // Load daily overstock queries
  const loadDailyQueries = useCallback(async () => {
    try {
      setLoadingDaily(true);
      const response = await api.get(`${API}/stock-management/queries`);
      setDailyQueries(response.data.queries || []);
    } catch (error) {
      console.error('Error loading daily queries:', error);
      toast.error('Failed to load queries');
    } finally {
      setLoadingDaily(false);
    }
  }, []);
  
  // Load weekly stock reports
  const loadWeeklyReports = useCallback(async () => {
    try {
      setLoadingWeekly(true);
      const response = await api.get(`${API}/stock-management/weekly-stock/reports`, {
        params: { branch_id: effectiveBranchId }
      });
      const reports = response.data.reports || [];
      setWeeklyReports(reports);
      
      // Auto-select latest report - pass reports directly since state hasn't updated yet
      if (reports.length > 0) {
        const latestMonth = reports[0];
        setSelectedMonth(latestMonth.month_year);
        if (latestMonth.weeks?.length > 0) {
          loadWeeklyReportItems(latestMonth.weeks[0].id, reports);
        }
      }
    } catch (error) {
      console.error('Error loading weekly reports:', error);
    } finally {
      setLoadingWeekly(false);
    }
  }, [effectiveBranchId]);
  
  // Load weekly report items
  const loadWeeklyReportItems = async (reportId, allReports = weeklyReports) => {
    try {
      const response = await api.get(`${API}/stock-management/weekly-stock/report/${reportId}`);
      setSelectedWeeklyReport(response.data.report);
      setAllWeeklyItems(response.data.items || []);
      setWeeklyItems(response.data.items || []);
      
      // Find and load previous week's items for "New Lines" filter
      const currentReport = response.data.report;
      if (currentReport && allReports.length > 0) {
        // Get all weeks flattened and sorted by date
        const allWeeks = allReports.flatMap(m => m.weeks || []).sort((a, b) => 
          new Date(b.week_commencing) - new Date(a.week_commencing)
        );
        
        // Find current week index and get previous week
        const currentIndex = allWeeks.findIndex(w => w.id === reportId);
        if (currentIndex >= 0 && currentIndex < allWeeks.length - 1) {
          const prevWeek = allWeeks[currentIndex + 1];
          try {
            const prevResponse = await api.get(`${API}/stock-management/weekly-stock/report/${prevWeek.id}`);
            setPreviousWeekItems(prevResponse.data.items || []);
          } catch {
            setPreviousWeekItems([]);
          }
        } else {
          setPreviousWeekItems([]);
        }
      }
    } catch (error) {
      console.error('Error loading weekly report:', error);
      toast.error('Failed to load report');
    }
  };
  
  useEffect(() => {
    loadSummary();
    loadMasterStockData();
  }, [loadSummary, loadMasterStockData]);
  
  useEffect(() => {
    if (currentView === 'daily-overstock') {
      loadDailyQueries();
    } else if (currentView === 'weekly-stock') {
      loadWeeklyReports();
    }
  }, [currentView, loadDailyQueries, loadWeeklyReports]);
  
  // ================== CLIENT-SIDE FILTERING (Live Search) ==================
  const filteredWeeklyItems = useMemo(() => {
    let items = [...allWeeklyItems];
    
    // Apply search filter - same logic as global search
    if (weeklySearchQuery.trim()) {
      const searchLower = weeklySearchQuery.toLowerCase().trim();
      
      // Check if search matches a manual supplier group name and get its prefixes
      let matchedPrefixes = [];
      for (const group of manualSupplierGroups) {
        const groupName = (group.name || '').toLowerCase();
        // Match if group name starts with search OR search starts with group name
        if (groupName.startsWith(searchLower) || searchLower.startsWith(groupName.split(' ')[0])) {
          // Get prefixes from this supplier group
          const prefixes = group.prefixes || [];
          matchedPrefixes.push(...prefixes);
        }
      }
      
      items = items.filter(item => {
        const code = (item.product_code || '').toLowerCase();
        const codeUpper = (item.product_code || '').toUpperCase();
        const desc = (item.product_description || '').toLowerCase();
        
        // Direct match on code or description
        if (code.includes(searchLower) || desc.includes(searchLower)) {
          return true;
        }
        
        // Check if product code starts with any matched supplier group prefix
        if (matchedPrefixes.length > 0) {
          for (const prefix of matchedPrefixes) {
            if (codeUpper.startsWith(prefix)) {
              return true;
            }
          }
        }
        
        return false;
      });
    }
    
    // Apply status filters
    if (activeFilters.status_critical) {
      items = items.filter(i => i.status === 'critical');
    }
    if (activeFilters.status_monitor) {
      items = items.filter(i => i.status === 'monitor');
    }
    if (activeFilters.status_ok) {
      items = items.filter(i => i.status === 'ok');
    }
    if (activeFilters.status_resolved) {
      items = items.filter(i => i.status === 'resolved');
    }
    
    // Apply multiple filters (AND logic)
    if (activeFilters.fifty_plus_days) {
      items = items.filter(i => 
        (i.stock_days || '').includes('50') ||
        (i.category || '').includes('50')
      );
    }
    
    if (activeFilters.no_sales) {
      items = items.filter(i => 
        (i.stock_days || '').toLowerCase().includes('no sale') ||
        (i.category || '').toLowerCase().includes('no sale')
      );
    }
    
    if (activeFilters.value_above && filterValueAbove) {
      const threshold = parseFloat(filterValueAbove) || 0;
      items = items.filter(i => (i.stock_value || 0) >= threshold);
    }
    
    if (activeFilters.product_code && filterProductCode) {
      const prefix = filterProductCode.toLowerCase();
      items = items.filter(i => (i.product_code || '').toLowerCase().startsWith(prefix));
    }
    
    if (activeFilters.has_notes) {
      items = items.filter(i => (i.notes || '').trim());
    }
    
    if (activeFilters.no_notes) {
      items = items.filter(i => !(i.notes || '').trim());
    }
    
    // With reminders filter - show only items that have reminders
    if (activeFilters.has_reminder) {
      const reminderItemIds = new Set(weeklyReminders.map(r => r.item_id));
      items = items.filter(i => i.has_reminder || reminderItemIds.has(i.id));
    }
    
    // New lines filter - show only items not in previous week
    if (activeFilters.new_lines && previousWeekItems.length > 0) {
      const prevCodes = new Set(previousWeekItems.map(i => i.product_code?.toUpperCase()));
      items = items.filter(i => !prevCodes.has(i.product_code?.toUpperCase()));
    }
    
    // 51+ Days filter - more specific than 50+ days
    if (activeFilters.fifty_one_plus_days) {
      items = items.filter(i => {
        const days = i.stock_days || '';
        // Match "51+", "51-100", ">50", "No Sale" etc.
        const numMatch = days.match(/(\d+)/);
        if (numMatch) {
          const num = parseInt(numMatch[1], 10);
          if (num >= 51) return true;
        }
        // Also include "No Sale" items as they're typically old stock
        if (days.toLowerCase().includes('no sale')) return true;
        return false;
      });
    }
    
    // Sort by value if filter is active
    if (activeFilters.sort_by_value) {
      items = items.sort((a, b) => (b.stock_value || 0) - (a.stock_value || 0));
    }
    
    return items;
  }, [allWeeklyItems, weeklySearchQuery, activeFilters, filterValueAbove, filterProductCode, masterStockData, manualSupplierGroups, previousWeekItems, weeklyReminders]);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredWeeklyItems.length / itemsPerPage);
  const paginatedItems = useMemo(() => {
    const startIndex = (tablePage - 1) * itemsPerPage;
    return filteredWeeklyItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredWeeklyItems, tablePage, itemsPerPage]);
  
  // Reset page when filters change
  useEffect(() => {
    setTablePage(1);
  }, [weeklySearchQuery, activeFilters, filterValueAbove, filterProductCode]);
  
  // Update displayed items when filter changes
  useEffect(() => {
    setWeeklyItems(filteredWeeklyItems);
  }, [filteredWeeklyItems]);
  
  // Toggle filter
  const toggleFilter = (filterName) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };
  
  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;
  
  // ================== DAILY OVERSTOCK HANDLERS ==================
  const handleDailyFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploadingDaily(true);
    setShowDailyUploadModal(false);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branch_id', effectiveBranchId || 'C30');
    
    try {
      const response = await api.post(`${API}/stock-management/upload-overstock`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDailyUploadedData(response.data.items || []);
      setCurrentDailyQueryId(response.data.query_id);
      setShowDailyReviewModal(true);
      toast.success(`Uploaded ${response.data.items?.length || 0} items`);
      loadDailyQueries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingDaily(false);
      if (dailyFileInputRef.current) dailyFileInputRef.current.value = '';
    }
  };
  
  const openDailyQueryForEditing = async (query) => {
    try {
      const response = await api.get(`${API}/stock-management/queries/${query.id}/items`);
      setSelectedDailyQuery(query);
      setDailyQueryItems(response.data.items || []);
      setShowDailyReviewModal(true);
    } catch (error) {
      toast.error('Failed to load query items');
    }
  };
  
  const saveDailyQueryAndShowSummary = async () => {
    const queryId = selectedDailyQuery?.id || currentDailyQueryId;
    const items = selectedDailyQuery ? dailyQueryItems : dailyUploadedData;
    
    const itemsWithNotes = items.filter(i => i.notes?.trim()).length;
    const suppliers = [...new Set(items.map(i => i.supplier))];
    
    // Count statuses
    const criticalCount = items.filter(i => i.status === 'critical').length;
    const concernCount = items.filter(i => i.status === 'concern').length;
    const okCount = items.filter(i => i.status === 'ok').length;
    
    setDailySummaryData({
      date: selectedDailyQuery?.upload_date || new Date().toISOString(),
      total_items: items.length,
      items_with_notes: itemsWithNotes,
      suppliers,
      critical_count: criticalCount,
      concern_count: concernCount,
      ok_count: okCount
    });
    
    // Save all items with their statuses
    try {
      await api.put(`${API}/stock-management/queries/${queryId}/items`, { items });
    } catch (error) {
      console.error('Error saving items:', error);
    }
    
    // Update query status and counts
    await api.put(`${API}/stock-management/queries/${queryId}`, { 
      status: 'Complete',
      critical_count: criticalCount,
      concern_count: concernCount,
      ok_count: okCount
    });
    loadDailyQueries();
    setShowDailyReviewModal(false);
    setShowDailySummaryModal(true);
  };
  
  // Download Daily Overstock PDF
  const downloadDailyPDF = async (query) => {
    if (!query) return;
    
    try {
      // Get items for this query
      const response = await api.get(`${API}/stock-management/queries/${query.id}/items`);
      const items = response.data.items || [];
      
      // Dynamic import for jsPDF and autoTable
      const { default: jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(238, 114, 3);
      doc.text('Daily Purchasing Overstock Report', 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Date: ${safeFormatDate(query.upload_date, 'dd MMM yyyy')}`, 14, 30);
      doc.text(`Status: ${query.status}`, 14, 37);
      
      // Summary stats
      doc.setFontSize(10);
      doc.text(`Total Items: ${items.length}`, 14, 50);
      doc.text(`Suppliers: ${query.supplier_count || 0}`, 14, 57);
      
      const critical = items.filter(i => i.status === 'critical').length;
      const concern = items.filter(i => i.status === 'concern').length;
      const ok = items.filter(i => i.status === 'ok').length;
      
      doc.setTextColor(255, 0, 0);
      doc.text(`Critical: ${critical}`, 100, 50);
      doc.setTextColor(255, 165, 0);
      doc.text(`Not of Concern: ${concern}`, 130, 50);
      doc.setTextColor(0, 128, 0);
      doc.text(`OK: ${ok}`, 160, 50);
      doc.setTextColor(0, 0, 0);
      
      // Sort items: Critical first, then Concern, then OK, then the rest
      const sortedItems = [...items].sort((a, b) => {
        const statusOrder = { 'critical': 0, 'concern': 1, 'ok': 2 };
        const aOrder = statusOrder[a.status] ?? 3;
        const bOrder = statusOrder[b.status] ?? 3;
        return aOrder - bOrder;
      });
      
      // Table data
      const tableData = sortedItems.map(item => [
        item.order_number || '',
        item.supplier || '',
        (item.product || '').substring(0, 35),
        item.quantity || '',
        item.current_stock || '',
        item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '-',
        (item.notes || '').substring(0, 40)
      ]);
      
      autoTable(doc, {
        startY: 70,
        head: [['Order', 'Supplier', 'Product', 'Qty', 'Stock', 'Status', 'Notes']],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [238, 114, 3], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 28 },
          2: { cellWidth: 40 },
          3: { cellWidth: 12, halign: 'right' },
          4: { cellWidth: 12, halign: 'right' },
          5: { cellWidth: 15, halign: 'center' },
          6: { cellWidth: 55 }
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 5) {
            const status = data.cell.raw?.toLowerCase();
            if (status === 'critical') {
              data.cell.styles.fillColor = [255, 220, 220];
              data.cell.styles.textColor = [180, 0, 0];
            } else if (status === 'concern') {
              data.cell.styles.fillColor = [255, 250, 220];
              data.cell.styles.textColor = [180, 130, 0];
            } else if (status === 'ok') {
              data.cell.styles.fillColor = [220, 255, 220];
              data.cell.styles.textColor = [0, 128, 0];
            }
          }
        },
        margin: { left: 14, right: 14 }
      });
      
      // Save
      const dateStr = safeFormatDate(query.upload_date, 'yyyy-MM-dd');
      doc.save(`daily-overstock-${dateStr}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };
  
  // Load Complete Report Data
  const loadCompleteReportData = async () => {
    setLoadingCompleteReport(true);
    try {
      const response = await api.get(`${API}/stock-management/queries`, {
        params: { branch_id: effectiveBranchId }
      });
      const queries = response.data.queries || [];
      
      // Group by month
      const monthGroups = {};
      queries.forEach(q => {
        const date = new Date(q.upload_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthName = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        if (!monthGroups[monthKey]) {
          monthGroups[monthKey] = { key: monthKey, name: monthName, weeks: [] };
        }
        monthGroups[monthKey].weeks.push(q);
      });
      
      setCompleteReportMonths(Object.values(monthGroups).sort((a, b) => b.key.localeCompare(a.key)));
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoadingCompleteReport(false);
    }
  };
  
  // Generate Complete Report
  const generateCompleteReport = async () => {
    if (selectedReportWeeks.length === 0 && selectedReportMonths.length === 0) {
      toast.error('Please select at least one week or month');
      return;
    }
    
    setLoadingCompleteReport(true);
    try {
      // Get all selected query IDs
      let queryIds = [...selectedReportWeeks];
      
      // Add all weeks from selected months
      completeReportMonths.forEach(month => {
        if (selectedReportMonths.includes(month.key)) {
          month.weeks.forEach(w => {
            if (!queryIds.includes(w.id)) queryIds.push(w.id);
          });
        }
      });
      
      // Fetch all items
      const allItems = [];
      for (const qid of queryIds) {
        const response = await api.get(`${API}/stock-management/queries/${qid}/items`);
        const query = completeReportMonths.flatMap(m => m.weeks).find(w => w.id === qid);
        (response.data.items || []).forEach(item => {
          allItems.push({ ...item, query_date: query?.upload_date });
        });
      }
      
      // Sort: Critical first, then Concern, then OK, then the rest
      allItems.sort((a, b) => {
        const statusOrder = { 'critical': 0, 'concern': 1, 'ok': 2 };
        const aOrder = statusOrder[a.status] ?? 3;
        const bOrder = statusOrder[b.status] ?? 3;
        return aOrder - bOrder;
      });
      
      setCompleteReportData({
        items: allItems,
        total: allItems.length,
        critical: allItems.filter(i => i.status === 'critical').length,
        concern: allItems.filter(i => i.status === 'concern').length,
        ok: allItems.filter(i => i.status === 'ok').length,
        suppliers: [...new Set(allItems.map(i => i.supplier))]
      });
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoadingCompleteReport(false);
    }
  };
  
  // Download Complete Report PDF
  const downloadCompleteReportPDF = async () => {
    if (!completeReportData) return;
    
    try {
      // Dynamic import for jsPDF and autoTable
      const { default: jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      
      const doc = new jsPDF('l'); // Landscape
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(238, 114, 3);
      doc.text('Full Overstock Report', 14, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Generated: ${safeFormatDate(new Date(), 'dd MMM yyyy HH:mm')}`, 14, 28);
      doc.text(`Total Items: ${completeReportData.total} | Suppliers: ${completeReportData.suppliers.length}`, 14, 35);
      
      doc.setTextColor(255, 0, 0);
      doc.text(`Critical: ${completeReportData.critical}`, 150, 35);
      doc.setTextColor(255, 165, 0);
      doc.text(`Not of Concern: ${completeReportData.concern}`, 185, 35);
      doc.setTextColor(0, 128, 0);
      doc.text(`OK: ${completeReportData.ok}`, 220, 35);
      doc.setTextColor(0, 0, 0);
      
      const tableData = completeReportData.items.map(item => [
        safeFormatDate(item.query_date, 'dd/MM'),
        item.order_number || '',
        item.supplier || '',
        (item.product || '').substring(0, 40),
        item.quantity || '',
        item.current_stock || '',
        item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '-',
        (item.notes || '').substring(0, 50)
      ]);
      
      autoTable(doc, {
        startY: 45,
        head: [['Date', 'Order', 'Supplier', 'Product', 'Qty', 'Stock', 'Status', 'Notes']],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [238, 114, 3], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 18 },
          2: { cellWidth: 30 },
          3: { cellWidth: 55 },
          4: { cellWidth: 12, halign: 'right' },
          5: { cellWidth: 12, halign: 'right' },
          6: { cellWidth: 18, halign: 'center' },
          7: { cellWidth: 100 }
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 6) {
            const status = data.cell.raw?.toLowerCase();
            if (status === 'critical') {
              data.cell.styles.fillColor = [255, 220, 220];
              data.cell.styles.textColor = [180, 0, 0];
            } else if (status === 'concern') {
              data.cell.styles.fillColor = [255, 250, 220];
              data.cell.styles.textColor = [180, 130, 0];
            } else if (status === 'ok') {
              data.cell.styles.fillColor = [220, 255, 220];
              data.cell.styles.textColor = [0, 128, 0];
            }
          }
        },
        margin: { left: 14, right: 14 }
      });
      
      doc.save(`full-overstock-report-${safeFormatDate(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };
  
  // ================== WEEKLY STOCK HANDLERS ==================
  const handleWeeklyFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploadingWeekly(true);
    setShowWeeklyUploadModal(false);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branch_id', effectiveBranchId || 'C30');
    
    try {
      const response = await api.post(`${API}/stock-management/weekly-stock/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.items_count} items for week commencing ${response.data.week_commencing}`);
      loadWeeklyReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingWeekly(false);
      if (weeklyFileInputRef.current) weeklyFileInputRef.current.value = '';
    }
  };
  
  const handleWeeklyNoteChange = async (itemId, notes) => {
    // Update locally first - also mark as updated from previous if applicable
    setAllWeeklyItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, notes };
        // If this item has previous_week_notes and we're changing the note, mark it as updated
        if (item.previous_week_notes && notes !== item.previous_week_notes && notes !== '') {
          updatedItem.notes_updated_from_previous = true;
        }
        return updatedItem;
      }
      return item;
    }));
    
    // Auto-save
    setAutoSaving(true);
    try {
      await api.put(`${API}/stock-management/weekly-stock/item/${itemId}`, { notes });
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setAutoSaving(false);
    }
  };
  
  const handleWeeklyStatusChange = async (itemId, status) => {
    // Update locally first
    setAllWeeklyItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, status } : item
    ));
    
    // Auto-save
    setAutoSaving(true);
    try {
      await api.put(`${API}/stock-management/weekly-stock/item/${itemId}`, { status });
    } catch (error) {
      console.error('Error saving status:', error);
    } finally {
      setAutoSaving(false);
    }
  };
  
  const showWeeklyReportSummary = async () => {
    if (!selectedWeeklyReport) return;
    try {
      const [summaryRes, varianceRes] = await Promise.all([
        api.get(`${API}/stock-management/weekly-stock/report/${selectedWeeklyReport.id}/summary`),
        selectedMonth ? api.get(`${API}/stock-management/weekly-stock/month-variance`, { params: { month_year: selectedMonth } }) : Promise.resolve({ data: null })
      ]);
      setWeeklySummaryData(summaryRes.data);
      setWeeklyVarianceData(varianceRes.data);
      setShowWeeklySummaryModal(true);
    } catch (error) {
      toast.error('Failed to load summary');
    }
  };


  // ================== WEEKLY STOCK REMINDER FUNCTIONS ==================
  
  const loadWeeklyReminders = async () => {
    if (!selectedWeeklyReport) return;
    try {
      // Use include_all_weeks: true to show reminders from all weeks, not just the current report
      // This ensures reminders persist across week uploads and are visible on any week
      const [todayRes, prevRes, upcomingRes] = await Promise.all([
        api.get(`${API}/stock-management/weekly-stock/reminders`, { params: { report_id: selectedWeeklyReport.id, filter_type: 'today', include_all_weeks: true, branch_id: selectedWeeklyReport.branch_id } }),
        api.get(`${API}/stock-management/weekly-stock/reminders`, { params: { report_id: selectedWeeklyReport.id, filter_type: 'previous', include_all_weeks: true, branch_id: selectedWeeklyReport.branch_id } }),
        api.get(`${API}/stock-management/weekly-stock/reminders`, { params: { report_id: selectedWeeklyReport.id, filter_type: 'upcoming', include_all_weeks: true, branch_id: selectedWeeklyReport.branch_id } })
      ]);
      
      setTodayReminders(todayRes.data.reminders || []);
      setPreviousReminders(prevRes.data.reminders || []);
      setUpcomingReminders(upcomingRes.data.reminders || []);
      setWeeklyReminders([...(todayRes.data.reminders || []), ...(prevRes.data.reminders || []), ...(upcomingRes.data.reminders || [])]);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  useEffect(() => {
    if (selectedWeeklyReport) {
      loadWeeklyReminders();
    }
  }, [selectedWeeklyReport?.id]);

  const createReminder = async (item) => {
    if (!reminderDate || !reminderNote.trim()) {
      toast.error('Please enter a date and note for the reminder');
      return;
    }
    
    // Check if there's an existing reminder for this item
    const existingReminder = weeklyReminders.find(r => r.item_id === item.id);
    
    try {
      if (existingReminder) {
        // Update existing reminder
        await api.put(`${API}/stock-management/weekly-stock/reminders/${existingReminder.id}`, {
          reminder_date: reminderDate,
          reminder_time: reminderTime,
          reminder_note: reminderNote
        });
        toast.success('Reminder updated');
        
        // Clear filter if we were filtering by this reminder (date may have changed category)
        // But keep the item in view by not changing the table position
        if (filterByReminder?.id === existingReminder.id) {
          setFilterByReminder(null);
        }
      } else {
        // Create new reminder - include branch_id for proper filtering across weeks
        await api.post(`${API}/stock-management/weekly-stock/reminders`, {
          item_id: item.id,
          product_code: item.product_code,
          description: item.product_description,
          reminder_date: reminderDate,
          reminder_time: reminderTime,
          reminder_note: reminderNote,
          report_id: selectedWeeklyReport.id,
          branch_id: selectedWeeklyReport.branch_id
        });
        toast.success('Reminder created');
      }
      
      setActiveReminderItem(null);
      setShowDatePicker(false);
      setShowTimePicker(false);
      setReminderDate('');
      setReminderTime('08:00');
      setReminderNote('');
      loadWeeklyReminders();
      
      setAllWeeklyItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, has_reminder: true } : i
      ));
    } catch (error) {
      console.error('Error saving reminder:', error);
      toast.error('Failed to save reminder');
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await api.put(`${API}/stock-management/weekly-stock/reminders/${reminderId}/complete`);
      toast.success('Reminder completed');
      loadWeeklyReminders();
      setFilterByReminder(null);
      
      const reminder = weeklyReminders.find(r => r.id === reminderId);
      if (reminder) {
        setAllWeeklyItems(prev => prev.map(i => 
          i.id === reminder.item_id ? { ...i, has_reminder: false } : i
        ));
      }
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const filterToReminder = (reminder) => {
    // Toggle - if clicking same reminder, clear filter
    if (filterByReminder?.id === reminder.id) {
      setFilterByReminder(null);
    } else {
      setFilterByReminder(reminder);
    }
    // Keep dropdowns open - don't close them when clicking a reminder
  };

  const clearReminderFilter = () => {
    setFilterByReminder(null);
  };

  const formatReminderDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  
  const downloadWeeklyPDF = async () => {
    if (!weeklySummaryData) {
      toast.error('No summary data available');
      return;
    }
    
    // Close the modal immediately and show progress bar
    setShowWeeklySummaryModal(false);
    setPdfDownloading(true);
    setPdfProgress(10);
    
    try {
      console.log('Starting PDF generation...');
      setPdfProgress(20);
      
      // Dynamic import to ensure it loads properly
      const { default: jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      setPdfProgress(30);
      
      const doc = new jsPDF();
      const { report, summary, items } = weeklySummaryData;
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(238, 114, 3);
      doc.text('Weekly Stock Report', 14, 18);
      
      doc.setFontSize(11);
      doc.setTextColor(80, 80, 80);
      const weekCommencing = report?.week_commencing ? safeFormatDate(report.week_commencing, 'dd MMM yyyy') : 'N/A';
      const generatedDate = safeFormatDate(new Date(), 'dd MMM yyyy HH:mm');
      doc.text(`Week Commencing: ${weekCommencing}`, 14, 26);
      doc.text(`Generated: ${generatedDate}`, 120, 26);
      
      // Calculate values
      const totalEntries = summary?.total_entries || 0;
      const entriesWithNotes = summary?.entries_with_notes || 0;
      const entriesWithoutNotes = totalEntries - entriesWithNotes;
      const entries50Plus = summary?.entries_50_plus_days || 0;
      const criticalCount = summary?.critical_count || 0;
      const monitorCount = summary?.monitor_count || 0;
      const okCount = summary?.ok_count || 0;
      const resolvedCount = summary?.resolved_count || 0;
      const valueWithNotes = summary?.value_with_notes || 0;
      const valueCritical = summary?.value_critical || 0;
      const valueMonitor = summary?.value_monitor || 0;
      const valueOk = summary?.value_ok || 0;
      const valueResolved = summary?.value_resolved || 0;
      const totalValue = summary?.total_value || 0;
      const value51Plus = summary?.value_51_plus || 0;
      const entries51Plus = summary?.entries_51_plus_days || 0;
      const percentReducedThisWeek = summary?.percentage_reduced_this_week || 0;
      const percentReducedOverall = summary?.percentage_reduced_overall || 0;
      const totalResolvedValueOverall = summary?.total_resolved_value_overall || 0;
      const totalResolvedItemsOverall = summary?.total_resolved_items_overall || 0;
      const percentageCritical = summary?.percentage_critical || 0;
      const percentageMonitor = summary?.percentage_monitor || 0;
      const percentageOk = summary?.percentage_ok || 0;
      const percentageWithNotes = summary?.percentage_with_notes || 0;
      
      setPdfProgress(50);
      
      const formatCurrency = (val) => `£${Number(val || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
      
      // Card dimensions matching summary layout - 4 equal cards per row
      const cardWidth = 44;
      const cardHeight = 28;
      const cardGap = 3;
      const startX = 14;
      
      // Helper function to draw a gradient corner card (white bg, colored border, gradient in top-right)
      const drawGradientCard = (x, y, width, height, borderColor, gradientColor) => {
        // White fill
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, width, height, 4, 4, 'F');
        // Gradient corner overlay (top-right) - draw filled triangle using lines
        doc.setFillColor(gradientColor[0], gradientColor[1], gradientColor[2]);
        // Move to starting point and draw triangle
        doc.lines(
          [[12, 0], [0, 12], [-12, -12]],  // relative coordinates: right, down, diagonal back
          x + width - 12, y,                // starting point
          [1, 1],                           // scale
          'F'                               // fill
        );
        // Colored border
        doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        doc.setLineWidth(1.2);
        doc.roundedRect(x, y, width, height, 4, 4, 'S');
      };
      
      // ================== ROW 1: Performance KPIs ==================
      let y = 32;
      
      // THIS WEEK Card - Green border with gradient corner
      drawGradientCard(startX, y, cardWidth, cardHeight, [22, 163, 74], [220, 252, 231]);
      // Label
      doc.setFontSize(6);
      doc.setTextColor(22, 163, 74);
      doc.text('THIS WEEK', startX + 4, y + 6);
      // Main percentage
      doc.setFontSize(18);
      doc.setTextColor(22, 163, 74);
      doc.text(`${percentReducedThisWeek.toFixed(1)}%`, startX + 4, y + 15);
      // Value
      doc.setFontSize(9);
      doc.setTextColor(50, 50, 50);
      doc.text(formatCurrency(valueResolved), startX + 4, y + 21);
      // Sub-label
      doc.setFontSize(6);
      doc.setTextColor(120, 120, 120);
      doc.text(`${resolvedCount} lines resolved`, startX + 4, y + 26);
      
      // OVERALL Card - Blue border with gradient corner
      const x2 = startX + cardWidth + cardGap;
      drawGradientCard(x2, y, cardWidth, cardHeight, [37, 99, 235], [219, 234, 254]);
      // Label
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);
      doc.text('OVERALL', x2 + 4, y + 6);
      // Main percentage
      doc.setFontSize(18);
      doc.setTextColor(37, 99, 235);
      doc.text(`${percentReducedOverall.toFixed(1)}%`, x2 + 4, y + 15);
      // Value
      doc.setFontSize(9);
      doc.setTextColor(50, 50, 50);
      doc.text(formatCurrency(totalResolvedValueOverall), x2 + 4, y + 21);
      // Sub-label
      doc.setFontSize(6);
      doc.setTextColor(120, 120, 120);
      doc.text(`${totalResolvedItemsOverall} items cumulative`, x2 + 4, y + 26);
      
      // 51+ DAYS STOCK Card - Purple border with gradient corner
      const x3 = x2 + cardWidth + cardGap;
      drawGradientCard(x3, y, cardWidth, cardHeight, [126, 34, 206], [243, 232, 255]);
      // Label
      doc.setFontSize(6);
      doc.setTextColor(126, 34, 206);
      doc.text('51+ DAYS STOCK', x3 + 4, y + 6);
      // Main value
      doc.setFontSize(14);
      doc.setTextColor(126, 34, 206);
      doc.text(formatCurrency(value51Plus), x3 + 4, y + 14);
      // Sub-label
      doc.setFontSize(6);
      doc.setTextColor(126, 34, 206);
      doc.text('Target stock to reduce', x3 + 4, y + 20);
      // Item count
      doc.setFontSize(8);
      doc.text(`${entries51Plus} items`, x3 + 4, y + 26);
      
      // TOTAL VALUE Card - Orange border with gradient corner
      const x4 = x3 + cardWidth + cardGap;
      drawGradientCard(x4, y, cardWidth, cardHeight, [238, 114, 3], [255, 237, 213]);
      // Gradient corner overlay (orange)
      doc.setFillColor(255, 237, 213);
      doc.circle(x4 + cardWidth - 3, y + 3, 12, 'F');
      // Label
      doc.setFontSize(6);
      doc.setTextColor(238, 114, 3);
      doc.text('TOTAL VALUE', x4 + 4, y + 6);
      // Main value
      doc.setFontSize(14);
      doc.text(formatCurrency(totalValue), x4 + 4, y + 14);
      // Sub-label
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);
      doc.text('Total stock under review', x4 + 4, y + 20);
      // Line count
      doc.setFontSize(8);
      doc.setTextColor(50, 50, 50);
      doc.text(`${totalEntries} lines`, x4 + 4, y + 26);
      
      // ================== ROW 2: Status Cards (No Circles - Gradient Corner Style) ==================
      y = 64;
      const statusCardHeight = 22;
      
      // CRITICAL Card - Red border with gradient corner
      drawGradientCard(startX, y, cardWidth, statusCardHeight, [220, 38, 38], [254, 226, 226]);
      // Label
      doc.setFontSize(6);
      doc.setTextColor(220, 38, 38);
      doc.text('CRITICAL', startX + 4, y + 6);
      // Count and percentage
      doc.setFontSize(14);
      doc.text(String(criticalCount), startX + 4, y + 14);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`${percentageCritical.toFixed(0)}%`, startX + 18, y + 14);
      // Value
      doc.setFontSize(7);
      doc.setTextColor(220, 38, 38);
      doc.text(formatCurrency(valueCritical), startX + 4, y + 20);
      
      // NEED TO MONITOR Card - Yellow border with gradient corner
      drawGradientCard(x2, y, cardWidth, statusCardHeight, [202, 138, 4], [254, 249, 195]);
      // Label
      doc.setFontSize(5);
      doc.setTextColor(161, 98, 7);
      doc.text('NEED TO MONITOR', x2 + 4, y + 6);
      // Count and percentage
      doc.setFontSize(14);
      doc.text(String(monitorCount), x2 + 4, y + 14);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`${percentageMonitor.toFixed(0)}%`, x2 + 18, y + 14);
      // Value
      doc.setFontSize(7);
      doc.setTextColor(161, 98, 7);
      doc.text(formatCurrency(valueMonitor), x2 + 4, y + 20);
      
      // OK (NO CONCERN) Card - Orange border with gradient corner
      drawGradientCard(x3, y, cardWidth, statusCardHeight, [234, 88, 12], [255, 237, 213]);
      // Label
      doc.setFontSize(5);
      doc.setTextColor(234, 88, 12);
      doc.text('OK (NO CONCERN)', x3 + 4, y + 6);
      // Count and percentage
      doc.setFontSize(14);
      doc.text(String(okCount), x3 + 4, y + 14);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`${percentageOk.toFixed(0)}%`, x3 + 18, y + 14);
      // Value
      doc.setFontSize(7);
      doc.setTextColor(234, 88, 12);
      doc.text(formatCurrency(valueOk), x3 + 4, y + 20);
      
      // WITH NOTES Card - Blue border with gradient corner
      drawGradientCard(x4, y, cardWidth, statusCardHeight, [37, 99, 235], [219, 234, 254]);
      // Label
      doc.setFontSize(6);
      doc.setTextColor(37, 99, 235);
      doc.text('WITH NOTES', x4 + 4, y + 6);
      // Count and percentage
      doc.setFontSize(14);
      doc.text(String(entriesWithNotes), x4 + 4, y + 14);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`${percentageWithNotes.toFixed(0)}%`, x4 + 18, y + 14);
      // Value
      doc.setFontSize(7);
      doc.setTextColor(37, 99, 235);
      doc.text(formatCurrency(valueWithNotes), x4 + 4, y + 20);
      
      // ================== ROW 3: Quick Stats ==================
      y = 90;
      const statsCardWidth = 44;
      const statsCardHeight = 14;
      
      // Helper function for small stat cards with border and gradient
      const drawSmallGradientCard = (x, y, width, height, borderColor, gradientColor) => {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, width, height, 3, 3, 'F');
        // Small gradient corner - draw triangle using lines
        doc.setFillColor(gradientColor[0], gradientColor[1], gradientColor[2]);
        doc.lines(
          [[8, 0], [0, 8], [-8, -8]],  // right, down, diagonal back
          x + width - 8, y,
          [1, 1],
          'F'
        );
        doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        doc.setLineWidth(0.8);
        doc.roundedRect(x, y, width, height, 3, 3, 'S');
      };
      
      // TOTAL LINES Card - Gray border
      drawSmallGradientCard(startX, y, statsCardWidth, statsCardHeight, [150, 150, 150], [249, 250, 251]);
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.text(String(totalEntries), startX + statsCardWidth/2, y + 7, { align: 'center' });
      doc.setFontSize(5);
      doc.setTextColor(120, 120, 120);
      doc.text('TOTAL LINES', startX + statsCardWidth/2, y + 12, { align: 'center' });
      
      // 50+ DAYS Card - Orange border
      drawSmallGradientCard(x2, y, statsCardWidth, statsCardHeight, [238, 114, 3], [255, 237, 213]);
      doc.setFontSize(12);
      doc.setTextColor(238, 114, 3);
      doc.text(String(entries50Plus), x2 + statsCardWidth/2, y + 7, { align: 'center' });
      doc.setFontSize(5);
      doc.setTextColor(120, 120, 120);
      doc.text('50+ DAYS', x2 + statsCardWidth/2, y + 12, { align: 'center' });
      
      // RESOLVED Card - Green border
      drawSmallGradientCard(x3, y, statsCardWidth, statsCardHeight, [22, 163, 74], [220, 252, 231]);
      doc.setFontSize(12);
      doc.setTextColor(22, 163, 74);
      doc.text(String(resolvedCount), x3 + statsCardWidth/2, y + 7, { align: 'center' });
      doc.setFontSize(5);
      doc.setTextColor(22, 163, 74);
      doc.text('RESOLVED', x3 + statsCardWidth/2, y + 12, { align: 'center' });
      
      // NO NOTES Card - Gray border
      drawSmallGradientCard(x4, y, statsCardWidth, statsCardHeight, [150, 150, 150], [249, 250, 251]);
      doc.setFontSize(12);
      doc.setTextColor(120, 120, 120);
      doc.text(String(entriesWithoutNotes), x4 + statsCardWidth/2, y + 7, { align: 'center' });
      doc.setFontSize(5);
      doc.text('NO NOTES', x4 + statsCardWidth/2, y + 12, { align: 'center' });
      
      setPdfProgress(70);
      
      // ================== ITEMS TABLE ==================
      const itemsArray = Array.isArray(items) ? items : [];
      
      const itemsWithNotesOrStatus = itemsArray.filter(i => (i.notes || '').trim() || i.status);
      const itemsWithoutNotesArr = itemsArray.filter(i => !(i.notes || '').trim() && !i.status);
      
      // Sort items with notes by status priority: critical -> monitor -> ok -> resolved
      const sortedWithNotes = [...itemsWithNotesOrStatus].sort((a, b) => {
        const statusOrder = { 'critical': 0, 'monitor': 1, 'ok': 2, 'resolved': 3 };
        const aOrder = statusOrder[a.status] ?? 4;
        const bOrder = statusOrder[b.status] ?? 4;
        return aOrder - bOrder;
      });
      
      // Combine: items with notes first, then items without notes
      const allSortedItems = [...sortedWithNotes, ...itemsWithoutNotesArr];
      
      console.log('Items with notes/status:', sortedWithNotes.length);
      console.log('Items without notes:', itemsWithoutNotesArr.length);
      console.log('Total items for PDF:', allSortedItems.length);
      
      const tableData = allSortedItems.map(item => [
        String(item.product_code || ''),
        String(item.product_description || '').substring(0, 35),
        String((Number(item.stock_value) || 0).toFixed(0)),
        String(item.stock_quantity || '0'),
        String(item.stock_days || ''),
        item.status === 'critical' ? 'C' : item.status === 'monitor' ? 'M' : item.status === 'ok' ? 'O' : item.status === 'resolved' ? 'R' : '-',
        String(item.notes || '').substring(0, 40)
      ]);
      
      if (tableData.length > 0) {
        autoTable(doc, {
          startY: 108,
          head: [['Code', 'Description', 'Value', 'Qty', 'Days', 'Status', 'Notes']],
          body: tableData,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [238, 114, 3], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 45 },
            2: { cellWidth: 18, halign: 'right' },
            3: { cellWidth: 15, halign: 'right' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 12, halign: 'center' },
            6: { cellWidth: 50 }
          },
          didParseCell: function(data) {
            if (data.section === 'body' && data.column.index === 5) {
              const status = String(data.cell.raw || '').toLowerCase();
              if (status === 'c') { // Critical - Red
                data.cell.styles.fillColor = [255, 220, 220];
                data.cell.styles.textColor = [180, 0, 0];
              } else if (status === 'm') { // Monitor - Yellow
                data.cell.styles.fillColor = [255, 250, 220];
                data.cell.styles.textColor = [180, 130, 0];
              } else if (status === 'o') { // OK - Orange
                data.cell.styles.fillColor = [255, 237, 213];
                data.cell.styles.textColor = [234, 88, 12];
              } else if (status === 'r') { // Resolved - Green
                data.cell.styles.fillColor = [220, 252, 231];
                data.cell.styles.textColor = [22, 163, 74];
              }
            }
            // Grey out rows without notes
            if (data.section === 'body') {
              const notesValue = data.row.raw[6];
              const statusValue = data.row.raw[5];
              if ((!notesValue || notesValue === '') && statusValue === '-') {
                data.cell.styles.textColor = [150, 150, 150];
              }
            }
          },
          margin: { left: 14, right: 14 }
        });
      } else {
        doc.setFontSize(10);
        doc.text('No items to display.', 14, 100);
      }
      
      setPdfProgress(90);
      
      // Save the PDF
      let weekDate = 'report';
      try {
        if (report?.week_commencing) {
          weekDate = String(report.week_commencing).split('T')[0];
        } else {
          weekDate = new Date().toISOString().split('T')[0];
        }
      } catch (e) {
        weekDate = new Date().toISOString().split('T')[0];
      }
      
      console.log('Saving PDF with date:', weekDate);
      doc.save(`weekly-stock-report-${weekDate}.pdf`);
      setPdfProgress(100);
      
      // Clear progress after a brief delay
      setTimeout(() => {
        setPdfDownloading(false);
        setPdfProgress(0);
      }, 1000);
      
      toast.success('PDF downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error stack:', error.stack);
      toast.error(`Failed to generate PDF: ${error.message || 'Unknown error'}`);
      setPdfDownloading(false);
      setPdfProgress(0);
    }
  };
  
  const currentMonthData = weeklyReports.find(r => r.month_year === selectedMonth);
  
  // ================== RENDER HOME PAGE ==================
  const renderHomePage = () => (
    <div className="space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Stock Management</h2>
      </div>
      
      {/* Navigation Pills */}
      <div className="flex gap-3">
        <button
          onClick={() => setCurrentView('daily-overstock')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all bg-white border-2 hover:bg-orange-50"
          style={{ borderColor: enconOrange, color: enconOrange }}
        >
          <ShoppingCart className="w-4 h-4" />
          Daily Purchasing Overstock
        </button>
        <button
          onClick={() => setCurrentView('weekly-stock')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all bg-white border-2 hover:bg-orange-50"
          style={{ borderColor: enconOrange, color: enconOrange }}
        >
          <BarChart3 className="w-4 h-4" />
          Weekly Stock Report
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Daily Purchasing Overstock Card */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-2 bg-white"
          style={{ borderColor: enconOrange }}
          onClick={() => setCurrentView('daily-overstock')}
          data-testid="daily-overstock-card"
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" style={{ color: enconOrange }} />
              Daily Purchasing Overstock
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <div className="animate-pulse h-32 bg-gray-100 rounded" />
            ) : (
              <div className="space-y-3">
                {/* Analytics Row - Percentage Cards */}
                {summaryData?.daily_overstock?.analytics && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {/* Dealt With % */}
                    <div className="relative rounded-lg border-2 bg-white overflow-hidden" style={{ borderColor: '#22c55e' }}>
                      <div 
                        className="absolute top-0 right-0 w-0 h-0"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '0 24px 24px 0',
                          borderColor: 'transparent #dcfce7 transparent transparent'
                        }}
                      />
                      <div className="p-2">
                        <div className="text-[10px] uppercase tracking-wide text-green-600 font-medium">Dealt With</div>
                        <div className="text-xl font-bold text-green-600">
                          {summaryData.daily_overstock.analytics.percent_dealt_with}%
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {summaryData.daily_overstock.analytics.items_with_status} of {summaryData.daily_overstock.analytics.total_items}
                        </div>
                      </div>
                    </div>
                    {/* With Notes % */}
                    <div className="relative rounded-lg border-2 bg-white overflow-hidden" style={{ borderColor: '#3b82f6' }}>
                      <div 
                        className="absolute top-0 right-0 w-0 h-0"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '0 24px 24px 0',
                          borderColor: 'transparent #dbeafe transparent transparent'
                        }}
                      />
                      <div className="p-2">
                        <div className="text-[10px] uppercase tracking-wide text-blue-600 font-medium">With Notes</div>
                        <div className="text-xl font-bold text-blue-600">
                          {summaryData.daily_overstock.analytics.percent_with_notes}%
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {summaryData.daily_overstock.analytics.items_with_notes} items
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Existing Details */}
                <div className="space-y-1 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Queries</span>
                    <span className="font-semibold">{summaryData?.daily_overstock?.total_queries || 0}</span>
                  </div>
                  {summaryData?.daily_overstock?.latest && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Latest Upload</span>
                        <span className="font-semibold">
                          {safeFormatDate(summaryData.daily_overstock.latest.upload_date, 'dd MMM yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Status</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          summaryData.daily_overstock.latest.status === 'Complete' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {summaryData.daily_overstock.latest.status}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Weekly Stock Report Card */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-2 bg-white"
          style={{ borderColor: enconOrange }}
          onClick={() => setCurrentView('weekly-stock')}
          data-testid="weekly-stock-card"
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: enconOrange }} />
              Weekly Stock Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <div className="animate-pulse h-32 bg-gray-100 rounded" />
            ) : (
              <div className="space-y-3">
                {/* Analytics Row - Percentage Cards */}
                {summaryData?.weekly_stock?.analytics && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {/* This Week % */}
                    <div className="relative rounded-lg border-2 bg-white overflow-hidden" style={{ borderColor: '#22c55e' }}>
                      <div 
                        className="absolute top-0 right-0 w-0 h-0"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '0 24px 24px 0',
                          borderColor: 'transparent #dcfce7 transparent transparent'
                        }}
                      />
                      <div className="p-2">
                        <div className="text-[10px] uppercase tracking-wide text-green-600 font-medium">This Week</div>
                        <div className="text-xl font-bold text-green-600">
                          {summaryData.weekly_stock.analytics.percent_reduced_this_week}%
                        </div>
                        <div className="text-[10px] text-gray-500">
                          £{(summaryData.weekly_stock.analytics.value_resolved || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {/* Overall % */}
                    <div className="relative rounded-lg border-2 bg-white overflow-hidden" style={{ borderColor: '#3b82f6' }}>
                      <div 
                        className="absolute top-0 right-0 w-0 h-0"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '0 24px 24px 0',
                          borderColor: 'transparent #dbeafe transparent transparent'
                        }}
                      />
                      <div className="p-2">
                        <div className="text-[10px] uppercase tracking-wide text-blue-600 font-medium">Overall</div>
                        <div className="text-xl font-bold text-blue-600">
                          {summaryData.weekly_stock.analytics.percent_reduced_overall}%
                        </div>
                        <div className="text-[10px] text-gray-500">
                          £{(summaryData.weekly_stock.analytics.total_resolved_value_overall || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Existing Details */}
                <div className="space-y-1 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Reports</span>
                    <span className="font-semibold">{summaryData?.weekly_stock?.total_reports || 0}</span>
                  </div>
                  {summaryData?.weekly_stock?.latest && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Week Commencing</span>
                        <span className="font-semibold">
                          {safeFormatDate(summaryData.weekly_stock.latest.week_commencing, 'dd MMM yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Items</span>
                        <span className="font-semibold">{summaryData.weekly_stock.latest.total_items || 0}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
  
  // ================== RENDER DAILY OVERSTOCK PAGE ==================
  const renderDailyOverstockPage = () => (
    <div className="space-y-6 pt-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCurrentView('stock-ordering')}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h2 className="text-2xl font-bold text-gray-800">Daily Purchasing Overstock</h2>
        
        {/* Full Report Button */}
        <div className="ml-auto">
          <Button
            onClick={() => { loadCompleteReportData(); setShowCompleteReportModal(true); }}
            variant="outline"
            className="rounded-full gap-2 border-2"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <BarChart3 className="w-4 h-4" />
            Full Report
          </Button>
        </div>
      </div>
      
      {/* Upload Section */}
      <Card className="border-2 bg-white" style={{ borderColor: enconOrange }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5" style={{ color: enconOrange }} />
                Upload Daily Purchasing Overstock
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload an Excel file (.xlsx) containing overstock data.
              </p>
            </div>
            <input
              ref={dailyFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleDailyFileUpload(e.target.files[0])}
              className="hidden"
            />
            <Button
              onClick={() => setShowDailyUploadModal(true)}
              disabled={uploadingDaily}
              className="gap-2 rounded-full px-6"
              style={{ backgroundColor: enconOrange }}
            >
              {uploadingDaily ? 'Uploading...' : <><Upload className="w-4 h-4" /> Upload Spreadsheet</>}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Previous Queries */}
      <div>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          Previous Queries
        </h3>
        {loadingDaily ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : dailyQueries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No queries uploaded yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dailyQueries.map(query => (
              <Card 
                key={query.id}
                className="cursor-pointer hover:shadow-lg transition-all border-2 bg-white"
                style={{ borderColor: enconOrange }}
                onClick={() => openDailyQueryForEditing(query)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold flex items-center gap-2">
                      <Calendar className="w-4 h-4" style={{ color: enconOrange }} />
                      {safeFormatDate(query.upload_date, 'dd MMM yyyy')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      query.status === 'Complete' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {query.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <div className="flex justify-between"><span>Suppliers:</span><span>{query.supplier_count || 0}</span></div>
                    <div className="flex justify-between"><span>Products:</span><span>{query.item_count || 0}</span></div>
                    <div className="flex justify-between"><span>Notes Added:</span><span>{query.notes_count || 0}/{query.item_count || 0}</span></div>
                    {/* Status breakdown */}
                    {(query.critical_count > 0 || query.concern_count > 0 || query.ok_count > 0) && (
                      <div className="flex gap-2 mt-2 pt-2 border-t">
                        {query.critical_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{query.critical_count} Critical</span>
                        )}
                        {query.concern_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">{query.concern_count} Not of Concern</span>
                        )}
                        {query.ok_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">{query.ok_count} OK</span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Daily Upload Modal */}
      <Dialog open={showDailyUploadModal} onOpenChange={setShowDailyUploadModal}>
        <DialogContent className="max-w-lg bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-6 h-6" style={{ color: enconOrange }} />
              Upload Overstock Spreadsheet
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingDaily(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDraggingDaily(false); }}
              onDrop={(e) => { e.preventDefault(); setIsDraggingDaily(false); handleDailyFileUpload(e.dataTransfer.files[0]); }}
              onClick={() => dailyFileInputRef.current?.click()}
              className={`border-3 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                isDraggingDaily ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
              }`}
              style={{ borderWidth: '3px' }}
            >
              <div className="flex flex-col items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDraggingDaily ? 'bg-orange-500' : 'bg-orange-100'}`}>
                  <Upload className={`w-8 h-8 ${isDraggingDaily ? 'text-white' : 'text-orange-500'}`} />
                </div>
                <p className="text-lg font-semibold text-gray-700">
                  {isDraggingDaily ? 'Drop your file here' : 'Drag & drop your Excel file here'}
                </p>
                <p className="text-sm text-gray-500">or click to browse</p>
                <Button variant="outline" className="rounded-full px-6 border-2" style={{ borderColor: enconOrange, color: enconOrange }}>
                  <FileText className="w-4 h-4 mr-2" /> Browse Files
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-400 text-center mt-4">Supported: .xlsx, .xls</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDailyUploadModal(false)} className="rounded-full">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Daily Review Modal */}
      <Dialog open={showDailyReviewModal} onOpenChange={(open) => !open && setShowDailyReviewModal(false)}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" style={{ color: enconOrange }} />
              {selectedDailyQuery ? safeFormatDate(selectedDailyQuery.upload_date, 'dd MMM yyyy') : 'New Upload'} - Overstock Review
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 space-y-4 min-h-0">
            {(selectedDailyQuery ? dailyQueryItems : dailyUploadedData).length === 0 ? (
              <div className="text-center py-8 text-gray-500">No items</div>
            ) : (
              Object.entries(
                (selectedDailyQuery ? dailyQueryItems : dailyUploadedData).reduce((acc, item, idx) => {
                  const order = item.order_number || 'Unknown';
                  if (!acc[order]) acc[order] = [];
                  acc[order].push({ ...item, idx });
                  return acc;
                }, {})
              ).map(([orderNumber, items]) => (
                <div key={orderNumber} className="border-2 rounded-lg overflow-hidden" style={{ borderColor: enconOrange }}>
                  <div className="px-4 py-2 font-semibold text-white" style={{ backgroundColor: enconOrange }}>
                    Order: {orderNumber}
                  </div>
                  <div className="divide-y">
                    {items.map(item => (
                      <div key={item.id || item.idx} className={`p-4 grid grid-cols-1 md:grid-cols-12 gap-4 ${
                        item.status === 'critical' ? 'bg-red-50' : 
                        item.status === 'concern' ? 'bg-yellow-50' : 
                        item.status === 'ok' ? 'bg-green-50' : 'bg-white'
                      }`}>
                        <div className="md:col-span-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium">{item.supplier}</span>
                          </div>
                          <p className="text-sm text-gray-600">{item.product}</p>
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Qty: <strong>{item.quantity}</strong></span>
                            <span>Stock: <strong>{item.current_stock}</strong></span>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs text-gray-500 mb-2 block">Status</Label>
                          <div className="flex flex-col gap-1">
                            <label className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded transition-colors ${
                              item.status === 'ok' ? 'bg-green-200' : 'hover:bg-green-100'
                            }`}>
                              <input
                                type="radio"
                                name={`status-${item.id || item.idx}`}
                                checked={item.status === 'ok'}
                                onChange={() => {
                                  const newItems = (selectedDailyQuery ? dailyQueryItems : dailyUploadedData).map(i => 
                                    (i.id || i.idx) === (item.id || item.idx) ? { ...i, status: 'ok' } : i
                                  );
                                  selectedDailyQuery ? setDailyQueryItems(newItems) : setDailyUploadedData(newItems);
                                }}
                                className="w-4 h-4 text-green-500"
                              />
                              <span className="text-sm text-green-700">OK</span>
                            </label>
                            <label className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded transition-colors ${
                              item.status === 'concern' ? 'bg-yellow-200' : 'hover:bg-yellow-100'
                            }`}>
                              <input
                                type="radio"
                                name={`status-${item.id || item.idx}`}
                                checked={item.status === 'concern'}
                                onChange={() => {
                                  const newItems = (selectedDailyQuery ? dailyQueryItems : dailyUploadedData).map(i => 
                                    (i.id || i.idx) === (item.id || item.idx) ? { ...i, status: 'concern' } : i
                                  );
                                  selectedDailyQuery ? setDailyQueryItems(newItems) : setDailyUploadedData(newItems);
                                }}
                                className="w-4 h-4 text-yellow-500"
                              />
                              <span className="text-sm text-yellow-700">Not of Concern</span>
                            </label>
                            <label className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded transition-colors ${
                              item.status === 'critical' ? 'bg-red-200' : 'hover:bg-red-100'
                            }`}>
                              <input
                                type="radio"
                                name={`status-${item.id || item.idx}`}
                                checked={item.status === 'critical'}
                                onChange={() => {
                                  const newItems = (selectedDailyQuery ? dailyQueryItems : dailyUploadedData).map(i => 
                                    (i.id || i.idx) === (item.id || item.idx) ? { ...i, status: 'critical' } : i
                                  );
                                  selectedDailyQuery ? setDailyQueryItems(newItems) : setDailyUploadedData(newItems);
                                }}
                                className="w-4 h-4 text-red-500"
                              />
                              <span className="text-sm text-red-700">Critical</span>
                            </label>
                          </div>
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-xs text-gray-500">Notes</Label>
                          <Textarea
                            value={item.notes || ''}
                            onChange={(e) => {
                              const newItems = (selectedDailyQuery ? dailyQueryItems : dailyUploadedData).map(i => 
                                (i.id || i.idx) === (item.id || item.idx) ? { ...i, notes: e.target.value } : i
                              );
                              selectedDailyQuery ? setDailyQueryItems(newItems) : setDailyUploadedData(newItems);
                            }}
                            placeholder="Add notes..."
                            className="mt-1 min-h-[80px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="border-t pt-4 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDailyReviewModal(false)} className="rounded-full"><X className="w-4 h-4 mr-2" />Close</Button>
            <Button onClick={saveDailyQueryAndShowSummary} className="rounded-full" style={{ backgroundColor: enconOrange }}>
              <Save className="w-4 h-4 mr-2" />Save & View Summary
            </Button>
            {selectedDailyQuery && (
              <Button onClick={() => downloadDailyPDF(selectedDailyQuery)} variant="outline" className="rounded-full border-2" style={{ borderColor: enconOrange, color: enconOrange }}>
                <FileText className="w-4 h-4 mr-2" />Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Daily Summary Modal */}
      <Dialog open={showDailySummaryModal} onOpenChange={setShowDailySummaryModal}>
        <DialogContent className="max-w-2xl bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Query Summary
            </DialogTitle>
          </DialogHeader>
          {dailySummaryData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Date</div>
                  <div className="text-lg font-semibold">{safeFormatDate(dailySummaryData.date, 'dd MMM yyyy')}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Total Items</div>
                  <div className="text-lg font-semibold">{dailySummaryData.total_items}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Suppliers</div>
                  <div className="text-lg font-semibold">{dailySummaryData.suppliers?.length || 0}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Notes Added</div>
                  <div className="text-lg font-semibold">{dailySummaryData.items_with_notes}/{dailySummaryData.total_items}</div>
                </div>
              </div>
              {/* Status Summary */}
              <div className="flex gap-3">
                <div className="flex-1 p-3 bg-red-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{dailySummaryData.critical_count || 0}</div>
                  <div className="text-sm text-red-600">Critical</div>
                </div>
                <div className="flex-1 p-3 bg-yellow-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-600">{dailySummaryData.concern_count || 0}</div>
                  <div className="text-sm text-yellow-600">Not of Concern</div>
                </div>
                <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{dailySummaryData.ok_count || 0}</div>
                  <div className="text-sm text-green-600">OK</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Suppliers:</h4>
                <div className="flex flex-wrap gap-2">
                  {dailySummaryData.suppliers?.map((s, idx) => (
                    <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDailySummaryModal(false)} className="rounded-full">Close</Button>
            {selectedDailyQuery && (
              <Button 
                onClick={() => downloadDailyPDF(selectedDailyQuery)} 
                className="rounded-full gap-2" 
                style={{ backgroundColor: enconOrange }}
              >
                <FileText className="w-4 h-4" />Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Full Report Modal */}
      <Dialog open={showCompleteReportModal} onOpenChange={setShowCompleteReportModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: enconOrange }} />
              Full Overstock Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 min-h-0 pr-2">
            {/* Month/Week Selection */}
            <div className="border-2 rounded-xl p-4" style={{ borderColor: enconOrange }}>
              <h4 className="font-semibold mb-3">Select Date Range</h4>
              {loadingCompleteReport ? (
                <div className="text-center py-4 text-gray-500">Loading...</div>
              ) : completeReportMonths.length === 0 ? (
                <div className="text-center py-4 text-gray-500">No data available</div>
              ) : (
                <div className="space-y-3">
                  {completeReportMonths.map(month => (
                    <div key={month.key} className="border rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center gap-3 px-4 py-2 bg-gray-50 cursor-pointer hover:bg-orange-50 transition-colors"
                        onClick={() => {
                          if (selectedReportMonths.includes(month.key)) {
                            setSelectedReportMonths(prev => prev.filter(m => m !== month.key));
                          } else {
                            setSelectedReportMonths(prev => [...prev, month.key]);
                          }
                        }}
                      >
                        <Checkbox
                          checked={selectedReportMonths.includes(month.key)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedReportMonths(prev => [...prev, month.key]);
                            } else {
                              setSelectedReportMonths(prev => prev.filter(m => m !== month.key));
                            }
                          }}
                          className="border-orange-300 data-[state=checked]:bg-orange-500"
                        />
                        <span className="font-medium">{month.name}</span>
                        <span className="text-sm text-gray-500">({month.weeks.length} weeks)</span>
                      </div>
                      <div className="px-4 py-2 space-y-1">
                        {month.weeks.map(week => (
                          <div 
                            key={week.id} 
                            className={`flex items-center gap-3 pl-6 py-1 cursor-pointer hover:bg-gray-50 rounded transition-colors ${selectedReportMonths.includes(month.key) ? 'opacity-50' : ''}`}
                            onClick={() => {
                              if (selectedReportMonths.includes(month.key)) return; // Disabled when month selected
                              if (selectedReportWeeks.includes(week.id)) {
                                setSelectedReportWeeks(prev => prev.filter(w => w !== week.id));
                              } else {
                                setSelectedReportWeeks(prev => [...prev, week.id]);
                              }
                            }}
                          >
                            <Checkbox
                              checked={selectedReportWeeks.includes(week.id) || selectedReportMonths.includes(month.key)}
                              disabled={selectedReportMonths.includes(month.key)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedReportWeeks(prev => [...prev, week.id]);
                                } else {
                                  setSelectedReportWeeks(prev => prev.filter(w => w !== week.id));
                                }
                              }}
                              className="border-gray-300 data-[state=checked]:bg-gray-500"
                            />
                            <span className="text-sm">W/C {safeFormatDate(week.upload_date, 'do MMM')}</span>
                            {week.critical_count > 0 && <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">{week.critical_count}</span>}
                            {week.concern_count > 0 && <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">{week.concern_count}</span>}
                            {week.ok_count > 0 && <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">{week.ok_count}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={generateCompleteReport}
                  disabled={loadingCompleteReport || (selectedReportMonths.length === 0 && selectedReportWeeks.length === 0)}
                  className="rounded-full gap-2"
                  style={{ backgroundColor: enconOrange }}
                >
                  <FileText className="w-4 h-4" />
                  {loadingCompleteReport ? 'Loading...' : 'Generate Report'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setSelectedReportMonths([]); setSelectedReportWeeks([]); setCompleteReportData(null); }}
                  className="rounded-full"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
            
            {/* Report Results */}
            {completeReportData && (
              <div className="border-2 rounded-xl overflow-hidden" style={{ borderColor: enconOrange }}>
                {/* Summary Header */}
                <div className="px-4 py-3 text-white flex items-center justify-between" style={{ backgroundColor: enconOrange }}>
                  <span className="font-semibold">Report Results</span>
                  <div className="flex gap-4 text-sm">
                    <span>Total: {completeReportData.total}</span>
                    <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded">{completeReportData.critical} Critical</span>
                    <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded">{completeReportData.concern} Not of Concern</span>
                    <span className="px-2 py-0.5 bg-green-200 text-green-800 rounded">{completeReportData.ok} OK</span>
                  </div>
                </div>
                
                {/* Results Table */}
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-100 text-sm">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Order</th>
                        <th className="px-3 py-2 text-left">Supplier</th>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {completeReportData.items.map((item, idx) => (
                        <tr key={idx} className={`border-t ${
                          item.status === 'critical' ? 'bg-red-50' :
                          item.status === 'concern' ? 'bg-yellow-50' :
                          item.status === 'ok' ? 'bg-green-50' : ''
                        }`}>
                          <td className="px-3 py-2">{safeFormatDate(item.query_date, 'dd/MM')}</td>
                          <td className="px-3 py-2">{item.order_number}</td>
                          <td className="px-3 py-2">{item.supplier}</td>
                          <td className="px-3 py-2 max-w-[200px] truncate" title={item.product}>{item.product}</td>
                          <td className="px-3 py-2 text-right">{item.quantity}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.status === 'critical' ? 'bg-red-200 text-red-700' :
                              item.status === 'concern' ? 'bg-yellow-200 text-yellow-700' :
                              item.status === 'ok' ? 'bg-green-200 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2 max-w-[200px] truncate" title={item.notes}>{item.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4 flex gap-2">
            <Button variant="outline" onClick={() => setShowCompleteReportModal(false)} className="rounded-full">Close</Button>
            {completeReportData && (
              <Button onClick={downloadCompleteReportPDF} className="rounded-full gap-2" style={{ backgroundColor: enconOrange }}>
                <Download className="w-4 h-4" />Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  // ================== RENDER WEEKLY STOCK PAGE ==================
  const renderWeeklyStockPage = () => (
    <div className="space-y-6 pt-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => setCurrentView('stock-ordering')} className="rounded-full">
          <ChevronLeft className="w-4 h-4 mr-1" />Back
        </Button>
        <h2 className="text-2xl font-bold text-gray-800">Weekly Stock Report</h2>
        
        {/* Upload Button */}
        <div className="ml-auto">
          <input ref={weeklyFileInputRef} type="file" accept=".xlsx,.xls" onChange={(e) => handleWeeklyFileUpload(e.target.files[0])} className="hidden" />
          <Button onClick={() => setShowWeeklyUploadModal(true)} disabled={uploadingWeekly} className="rounded-full gap-2" style={{ backgroundColor: enconOrange }}>
            <Upload className="w-4 h-4" />{uploadingWeekly ? 'Uploading...' : 'Upload Spreadsheet'}
          </Button>
        </div>
      </div>
      
      {/* Search & Filters Row */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[300px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by product code, description, supplier group..."
            value={weeklySearchQuery}
            onChange={(e) => setWeeklySearchQuery(e.target.value)}
            className="pl-10 rounded-full border-2 bg-white"
            style={{ borderColor: enconOrange }}
          />
        </div>
        
        {/* Multi-Select Filter Dropdown */}
        <div className="relative" ref={filterDropdownRef}>
          <Button
            variant="outline"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="rounded-full border-2 bg-white gap-2"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <Filter className="w-4 h-4" />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            <ChevronDown className="w-4 h-4" />
          </Button>
          {showFilterDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white border-2 rounded-xl shadow-lg z-20 min-w-[280px] p-3" style={{ borderColor: enconOrange }}>
              <div className="space-y-1">
                {/* New Lines Filter */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-blue-50" style={{ backgroundColor: activeFilters.new_lines ? 'rgb(239 246 255)' : '' }}>
                  <Checkbox
                    checked={activeFilters.new_lines}
                    onCheckedChange={() => toggleFilter('new_lines')}
                    className="border-blue-400 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                  />
                  <span className="text-sm font-medium text-blue-700">New Lines</span>
                  {previousWeekItems.length === 0 && <span className="text-xs text-gray-400">(no previous week)</span>}
                </label>
                
                <div className="border-t border-gray-200 my-2" />
                
                {/* Status Filters */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-red-50" style={{ backgroundColor: activeFilters.status_critical ? 'rgb(254 242 242)' : '' }}>
                  <Checkbox
                    checked={activeFilters.status_critical}
                    onCheckedChange={() => toggleFilter('status_critical')}
                    className="border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                  />
                  <span className="text-sm text-red-700">Critical</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-yellow-50" style={{ backgroundColor: activeFilters.status_monitor ? 'rgb(254 252 232)' : '' }}>
                  <Checkbox
                    checked={activeFilters.status_monitor}
                    onCheckedChange={() => toggleFilter('status_monitor')}
                    className="border-yellow-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                  />
                  <span className="text-sm text-yellow-700">Need to Monitor</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.status_ok ? 'rgb(255 247 237)' : '' }}>
                  <Checkbox
                    checked={activeFilters.status_ok}
                    onCheckedChange={() => toggleFilter('status_ok')}
                    className="border-orange-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  />
                  <span className="text-sm text-orange-700">OK (No Concern)</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-green-50" style={{ backgroundColor: activeFilters.status_resolved ? 'rgb(240 253 244)' : '' }}>
                  <Checkbox
                    checked={activeFilters.status_resolved}
                    onCheckedChange={() => toggleFilter('status_resolved')}
                    className="border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                  />
                  <span className="text-sm text-green-700">Resolved</span>
                </label>
                
                <div className="border-t border-gray-200 my-2" />
                
                {/* 50+ Days */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.fifty_plus_days ? 'rgb(255 247 237)' : '' }}>
                  <Checkbox
                    checked={activeFilters.fifty_plus_days}
                    onCheckedChange={() => toggleFilter('fifty_plus_days')}
                    className="border-orange-300 data-[state=checked]:bg-orange-500"
                  />
                  <span className="text-sm">50+ Days</span>
                </label>
                
                {/* No Sales */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.no_sales ? 'rgb(255 247 237)' : '' }}>
                  <Checkbox
                    checked={activeFilters.no_sales}
                    onCheckedChange={() => toggleFilter('no_sales')}
                    className="border-orange-300 data-[state=checked]:bg-orange-500"
                  />
                  <span className="text-sm">No Sales</span>
                </label>
                
                {/* Value Above */}
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.value_above ? 'rgb(255 247 237)' : '' }}>
                    <Checkbox
                      checked={activeFilters.value_above}
                      onCheckedChange={() => toggleFilter('value_above')}
                      className="border-orange-300 data-[state=checked]:bg-orange-500"
                    />
                    <span className="text-sm">Value Above</span>
                  </label>
                  {activeFilters.value_above && (
                    <Input
                      type="number"
                      placeholder="Enter value..."
                      value={filterValueAbove}
                      onChange={(e) => setFilterValueAbove(e.target.value)}
                      className="ml-8 w-[calc(100%-2rem)] rounded-full border-2 text-sm"
                      style={{ borderColor: enconOrange }}
                    />
                  )}
                </div>
                
                {/* Product Code */}
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.product_code ? 'rgb(255 247 237)' : '' }}>
                    <Checkbox
                      checked={activeFilters.product_code}
                      onCheckedChange={() => toggleFilter('product_code')}
                      className="border-orange-300 data-[state=checked]:bg-orange-500"
                    />
                    <span className="text-sm">Product Code Prefix</span>
                  </label>
                  {activeFilters.product_code && (
                    <Input
                      placeholder="Enter prefix..."
                      value={filterProductCode}
                      onChange={(e) => setFilterProductCode(e.target.value)}
                      className="ml-8 w-[calc(100%-2rem)] rounded-full border-2 text-sm"
                      style={{ borderColor: enconOrange }}
                    />
                  )}
                </div>
                
                {/* Has Notes */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.has_notes ? 'rgb(255 247 237)' : '' }}>
                  <Checkbox
                    checked={activeFilters.has_notes}
                    onCheckedChange={() => toggleFilter('has_notes')}
                    className="border-orange-300 data-[state=checked]:bg-orange-500"
                  />
                  <span className="text-sm">Has Notes</span>
                </label>
                
                {/* No Notes */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-orange-50" style={{ backgroundColor: activeFilters.no_notes ? 'rgb(255 247 237)' : '' }}>
                  <Checkbox
                    checked={activeFilters.no_notes}
                    onCheckedChange={() => toggleFilter('no_notes')}
                    className="border-orange-300 data-[state=checked]:bg-orange-500"
                  />
                  <span className="text-sm">No Notes</span>
                </label>
                
                {/* With Reminders */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-purple-50" style={{ backgroundColor: activeFilters.has_reminder ? 'rgb(250 245 255)' : '' }}>
                  <Checkbox
                    checked={activeFilters.has_reminder}
                    onCheckedChange={() => toggleFilter('has_reminder')}
                    className="border-purple-400 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  />
                  <div className="flex items-center gap-1">
                    <Bell className="w-3 h-3 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">With Reminders</span>
                  </div>
                </label>
                
                <div className="border-t border-gray-200 my-2" />
                
                {/* 51+ Days Filter */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-purple-50" style={{ backgroundColor: activeFilters.fifty_one_plus_days ? 'rgb(250 245 255)' : '' }}>
                  <Checkbox
                    checked={activeFilters.fifty_one_plus_days}
                    onCheckedChange={() => toggleFilter('fifty_one_plus_days')}
                    className="border-purple-400 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  />
                  <span className="text-sm font-medium text-purple-700">51+ Days (Target Stock)</span>
                </label>
                
                {/* Sort by Highest Value */}
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors hover:bg-indigo-50" style={{ backgroundColor: activeFilters.sort_by_value ? 'rgb(238 242 255)' : '' }}>
                  <Checkbox
                    checked={activeFilters.sort_by_value}
                    onCheckedChange={() => toggleFilter('sort_by_value')}
                    className="border-indigo-400 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                  />
                  <div className="flex items-center gap-1">
                    <ArrowUpDown className="w-3 h-3 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-700">Sort by Highest Value</span>
                  </div>
                </label>
                
                {/* Clear All */}
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setActiveFilters({
                        value_above: false,
                        no_sales: false,
                        fifty_plus_days: false,
                        fifty_one_plus_days: false,
                        product_code: false,
                        has_notes: false,
                        no_notes: false,
                        has_reminder: false,
                        status_critical: false,
                        status_monitor: false,
                        status_ok: false,
                        status_resolved: false,
                        new_lines: false,
                        sort_by_value: false
                      });
                      setFilterValueAbove('');
                      setFilterProductCode('');
                    }}
                    className="w-full text-sm text-gray-500 hover:text-orange-500 mt-2"
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        
        <Button onClick={showWeeklyReportSummary} variant="outline" className="rounded-full border-2 bg-white" style={{ borderColor: enconOrange, color: enconOrange }}>
          <Eye className="w-4 h-4 mr-2" />View Report
        </Button>
        
        {/* Save Button */}
        {selectedWeeklyReport && (
          <Button 
            onClick={async () => {
              try {
                const response = await api.post(`${API}/stock-management/manual-save`, {
                  report_id: selectedWeeklyReport.id,
                  name: `Manual Save - W/C ${safeFormatDate(selectedWeeklyReport.week_commencing, 'do MMM yyyy')}`
                });
                if (response.data.success) {
                  toast.success(`Saved! ${response.data.items_count} items backed up`);
                }
              } catch (error) {
                toast.error('Failed to save backup');
              }
            }}
            variant="outline" 
            className="rounded-full border-2 bg-white" 
            style={{ borderColor: '#3B82F6', color: '#3B82F6' }}
          >
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        )}
      </div>
      
      {/* Month/Week Selection Row */}
      <div className="flex gap-3 items-center">
        {/* Month Dropdown with nested weeks */}
        <div className="relative" ref={monthDropdownRef}>
          <Button
            variant="outline"
            onClick={() => setShowMonthDropdown(!showMonthDropdown)}
            className="rounded-full border-2 bg-white gap-2"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <Calendar className="w-4 h-4" />
            {currentMonthData?.month_name || 'Select Month'}
            {selectedWeeklyReport && (
              <span className="text-gray-500 font-normal">
                - W/C {safeFormatDate(selectedWeeklyReport.week_commencing, 'do MMM')}
              </span>
            )}
            <ChevronDown className="w-4 h-4" />
          </Button>
          {showMonthDropdown && weeklyReports.length > 0 && (
            <div className="absolute top-full left-0 mt-1 bg-white border-2 rounded-xl shadow-lg z-20 min-w-[280px] overflow-hidden" style={{ borderColor: enconOrange }}>
              {weeklyReports.map(monthData => (
                <div key={monthData.month_year}>
                  {/* Month Header */}
                  <div 
                    className={`px-4 py-2 font-semibold text-sm border-b ${
                      selectedMonth === monthData.month_year ? 'bg-orange-100' : 'bg-gray-50'
                    }`}
                    style={{ color: enconOrange }}
                  >
                    {monthData.month_name}
                  </div>
                  {/* Weeks under this month */}
                  {monthData.weeks?.map(week => (
                    <button
                      key={week.id}
                      onClick={() => {
                        setSelectedMonth(monthData.month_year);
                        loadWeeklyReportItems(week.id, weeklyReports);
                        setShowMonthDropdown(false);
                      }}
                      className={`w-full text-left px-6 py-2 hover:bg-orange-50 text-sm transition-colors ${
                        selectedWeeklyReport?.id === week.id ? 'bg-orange-100 font-semibold' : ''
                      }`}
                    >
                      W/C {safeFormatDate(week.week_commencing, 'do MMM')}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Auto-save indicator */}
        {autoSaving && <span className="text-xs text-gray-400 ml-auto">Auto-saving...</span>}
        
        {/* Results count */}
        {selectedWeeklyReport && (
          <span className="text-sm text-gray-500 ml-auto">
            Showing {weeklyItems.length} of {allWeeklyItems.length} items
          </span>
        )}
      </div>
      
      {/* Status Summary Header */}
      {selectedWeeklyReport && (
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl border-2 border-black">
          <span className="text-sm font-medium text-gray-600">Status Summary:</span>
          <div className="flex gap-3 flex-wrap">
            <span className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Critical: {allWeeklyItems.filter(i => i.status === 'critical').length}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
              Need to Monitor: {allWeeklyItems.filter(i => i.status === 'monitor').length}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              OK: {allWeeklyItems.filter(i => i.status === 'ok').length}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Resolved: {allWeeklyItems.filter(i => i.status === 'resolved').length}
            </span>
          </div>
          <span className="ml-auto text-sm text-gray-500">
            {allWeeklyItems.filter(i => i.notes_from_previous).length > 0 && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                {allWeeklyItems.filter(i => i.notes_from_previous).length} notes carried over
              </span>
            )}
          </span>
        </div>
      )}
      
      {/* Data Table */}
      {selectedWeeklyReport ? (
        <div className="relative">
          {/* Left Sidebar - Fixed, aligned with table */}
          <div className="fixed left-2 top-[340px] w-72 space-y-3 z-40">
            {/* Previous Reminders */}
            <div className="bg-white rounded-xl border-2 border-black overflow-hidden shadow-lg">
              <button
                onClick={() => setShowPreviousReminders(!showPreviousReminders)}
                className="w-full px-3 py-2 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <ChevronLeft className="h-4 w-4" />
                  Previous ({previousReminders.length})
                </span>
                {showPreviousReminders ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showPreviousReminders && previousReminders.length > 0 && (
                <div className="max-h-64 overflow-y-auto border-t">
                  {previousReminders.map(rem => (
                    <div 
                      key={rem.id}
                      onClick={() => !rem.product_delisted && filterToReminder(rem)}
                      className={`px-3 py-2 border-b last:border-b-0 ${
                        rem.product_delisted 
                          ? 'bg-gray-100 opacity-75' 
                          : filterByReminder?.id === rem.id 
                            ? 'bg-purple-100 border-l-4 border-l-purple-500 cursor-pointer' 
                            : 'hover:bg-red-50 cursor-pointer'
                      }`}
                    >
                      <p className="text-xs text-gray-600 truncate" title={rem.description}>{rem.description}</p>
                      <p className="text-xs text-gray-500 truncate">{rem.reminder_note}</p>
                      <p className="text-xs text-red-600 font-medium">{formatReminderDate(rem.reminder_date)}</p>
                      {rem.product_delisted && (
                        <p className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Product no longer tracked
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showPreviousReminders && previousReminders.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center">No previous reminders</div>
              )}
            </div>
            
            {/* Upcoming Reminders */}
            <div className="bg-white rounded-xl border-2 border-black overflow-hidden shadow-lg">
              <button
                onClick={() => setShowUpcomingReminders(!showUpcomingReminders)}
                className="w-full px-3 py-2 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  Upcoming ({upcomingReminders.length})
                  <ChevronRight className="h-4 w-4" />
                </span>
                {showUpcomingReminders ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showUpcomingReminders && upcomingReminders.length > 0 && (
                <div className="max-h-64 overflow-y-auto border-t">
                  {upcomingReminders.map(rem => (
                    <div 
                      key={rem.id}
                      onClick={() => !rem.product_delisted && filterToReminder(rem)}
                      className={`px-3 py-2 border-b last:border-b-0 ${
                        rem.product_delisted 
                          ? 'bg-gray-100 opacity-75' 
                          : filterByReminder?.id === rem.id 
                            ? 'bg-purple-100 border-l-4 border-l-purple-500 cursor-pointer' 
                            : 'hover:bg-green-50 cursor-pointer'
                      }`}
                    >
                      <p className="text-xs text-gray-600 truncate" title={rem.description}>{rem.description}</p>
                      <p className="text-xs text-gray-500 truncate">{rem.reminder_note}</p>
                      <p className="text-xs text-green-600 font-medium">{formatReminderDate(rem.reminder_date)}</p>
                      {rem.product_delisted && (
                        <p className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Product no longer tracked
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showUpcomingReminders && upcomingReminders.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center">No upcoming reminders</div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Fixed, aligned with table */}
          <div className="fixed right-2 top-[340px] w-72 z-40">
            <div className="bg-white rounded-xl border-2 border-black overflow-hidden shadow-lg">
              <button
                onClick={() => setShowTodayReminders(!showTodayReminders)}
                className="w-full px-3 py-2 flex items-center justify-between text-sm font-semibold bg-yellow-50 text-yellow-800 hover:bg-yellow-100"
              >
                <span className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Today ({todayReminders.length})
                </span>
                {showTodayReminders ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showTodayReminders && todayReminders.length > 0 && (
                <div className="max-h-80 overflow-y-auto border-t border-yellow-200">
                  {todayReminders.map(rem => (
                    <div 
                      key={rem.id}
                      className={`px-3 py-2 border-b border-yellow-100 last:border-b-0 ${
                        rem.product_delisted 
                          ? 'bg-gray-100 opacity-75' 
                          : filterByReminder?.id === rem.id 
                            ? 'bg-purple-100 border-l-4 border-l-purple-500' 
                            : 'hover:bg-yellow-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div 
                          className={`flex-1 ${rem.product_delisted ? '' : 'cursor-pointer'}`} 
                          onClick={() => !rem.product_delisted && filterToReminder(rem)}
                        >
                          <p className="text-xs text-gray-600 truncate" title={rem.description}>{rem.description}</p>
                          <p className="text-xs text-gray-700 mt-1">{rem.reminder_note}</p>
                          <p className="text-xs text-yellow-600 font-medium mt-1">{rem.reminder_time}</p>
                          {rem.product_delisted && (
                            <p className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Product no longer tracked
                            </p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            completeReminder(rem.id);
                          }}
                          className="ml-2 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600"
                          title="Complete reminder"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showTodayReminders && todayReminders.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500">No reminders for today</div>
              )}
            </div>
          </div>

          {/* Main Table - Original size and position */}
          <Card className="border-2 bg-white overflow-hidden rounded-2xl" style={{ borderColor: enconOrange }}>
            {/* Filter indicator */}
            {filterByReminder && (
              <div className="px-4 py-2 bg-purple-100 border-b flex items-center justify-between">
                <span className="text-sm text-purple-800">
                  <Bell className="h-4 w-4 inline mr-2" />
                  Filtered to reminder: <strong>{filterByReminder.product_code}</strong> - {filterByReminder.reminder_note}
                </span>
                <button 
                  onClick={clearReminderFilter}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                >
                  Clear Filter
                </button>
              </div>
            )}
            <div className="overflow-x-auto max-h-[550px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0">
                  <tr className="text-white text-sm" style={{ backgroundColor: enconOrange }}>
                    <th className="px-3 py-3 text-left w-[110px] rounded-tl-xl">Code</th>
                    <th className="px-3 py-3 text-left">Description</th>
                    <th className="px-3 py-3 text-center w-[50px]">
                      <Bell className="h-4 w-4 mx-auto" />
                    </th>
                    <th className="px-3 py-3 text-right w-[70px]">Value</th>
                    <th className="px-3 py-3 text-right w-[65px]">Qty</th>
                    <th className="px-3 py-3 text-center w-[90px]">Days</th>
                    <th className="px-3 py-3 text-center w-[110px]">Status</th>
                    <th className="px-3 py-3 text-left min-w-[280px] rounded-tr-xl">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(filterByReminder 
                    ? paginatedItems.filter(i => i.id === filterByReminder.item_id)
                    : paginatedItems
                  ).length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-500">No items found</td></tr>
                  ) : (
                    (filterByReminder 
                      ? paginatedItems.filter(i => i.id === filterByReminder.item_id)
                      : paginatedItems
                    ).map(item => {
                      const itemReminder = weeklyReminders.find(r => r.item_id === item.id);
                      const isReminderOpen = activeReminderItem === item.id;
                      
                      return (
                        <tr key={item.id} className={`border-b hover:bg-orange-50 transition-colors ${
                          item.status === 'critical' ? 'bg-red-50' : 
                          item.status === 'monitor' ? 'bg-yellow-50' : 
                          item.status === 'ok' ? 'bg-orange-50' : 
                          item.status === 'resolved' ? 'bg-green-50' : ''
                        }`}>
                          <td className="px-3 py-2 font-mono text-sm">{item.product_code}</td>
                          <td className="px-3 py-2 text-sm truncate max-w-[250px]" title={item.product_description}>{item.product_description}</td>
                          
                          {/* When reminder form is open, span across Bell, Value, Qty, Days, Status, Notes columns */}
                          {isReminderOpen ? (
                            <td colSpan={6} className="px-2 py-2">
                              <div className="relative">
                                <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-3 flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      createReminder(item);
                                    }}
                                    className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500 text-white hover:bg-purple-600 flex-shrink-0"
                                    title="Save & Close"
                                  >
                                    <Bell className="h-4 w-4" />
                                  </button>
                                  <div className="flex items-center gap-2 flex-1">
                                    {/* Date Button */}
                                    <button
                                      ref={dateButtonRef}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setPickerPosition({ top: rect.bottom + 8, left: rect.left });
                                        setShowDatePicker(!showDatePicker);
                                        setShowTimePicker(false);
                                      }}
                                      className="h-8 px-3 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <Calendar className="h-4 w-4 text-purple-500" />
                                      <span>{reminderDate ? format(new Date(reminderDate), 'dd MMM yyyy') : 'Select Date'}</span>
                                    </button>
                                    {/* Time Button */}
                                    <button
                                      ref={timeButtonRef}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setPickerPosition({ top: rect.bottom + 8, left: rect.left });
                                        setShowTimePicker(!showTimePicker);
                                        setShowDatePicker(false);
                                      }}
                                      className="h-8 px-3 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <Clock className="h-4 w-4 text-purple-500" />
                                      <span>{reminderTime || '08:00'}</span>
                                    </button>
                                    <div className="flex-1">
                                      <Input
                                        value={reminderNote}
                                        onChange={(e) => setReminderNote(e.target.value)}
                                        placeholder="Reminder note..."
                                        className="h-8 text-sm bg-white"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => createReminder(item)}
                                      className="h-8 bg-purple-500 hover:bg-purple-600 text-white"
                                    >
                                      {itemReminder ? 'Update' : 'Save'}
                                    </Button>
                                    <button
                                      onClick={() => {
                                        if (itemReminder) {
                                          completeReminder(itemReminder.id);
                                        }
                                        setActiveReminderItem(null);
                                        setShowDatePicker(false);
                                        setShowTimePicker(false);
                                      }}
                                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                                        itemReminder 
                                          ? 'border-green-400 text-green-500 hover:bg-green-100 hover:border-green-500' 
                                          : 'border-gray-300 text-gray-400 hover:bg-gray-100'
                                      }`}
                                      title={itemReminder ? "Complete & Remove Reminder" : "Close"}
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Date Picker Dropdown */}
                                {showDatePicker && (
                                  <div 
                                    className="fixed z-[9999] bg-white border-2 border-purple-300 rounded-xl shadow-2xl p-2"
                                    style={{ top: pickerPosition.top, left: pickerPosition.left }}
                                  >
                                    <CalendarPicker
                                      mode="single"
                                      selected={reminderDate ? new Date(reminderDate) : undefined}
                                      onSelect={(date) => {
                                        if (date) {
                                          setReminderDate(format(date, 'yyyy-MM-dd'));
                                        }
                                        setShowDatePicker(false);
                                      }}
                                      className="rounded-lg"
                                    />
                                  </div>
                                )}
                                
                                {/* Time Picker Dropdown */}
                                {showTimePicker && (
                                  <div 
                                    className="fixed z-[9999] bg-white border-2 border-purple-300 rounded-xl shadow-2xl p-3 max-h-64 overflow-y-auto"
                                    style={{ top: pickerPosition.top, left: pickerPosition.left }}
                                  >
                                    <div className="grid grid-cols-2 gap-1">
                                      {Array.from({ length: 28 }, (_, i) => {
                                        const hour = Math.floor(i / 2) + 8;
                                        const minute = (i % 2) * 30;
                                        if (hour > 20) return null;
                                        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                        return (
                                          <button
                                            key={timeStr}
                                            onClick={() => {
                                              setReminderTime(timeStr);
                                              setShowTimePicker(false);
                                            }}
                                            className={`px-3 py-1.5 text-sm rounded-lg hover:bg-purple-100 transition-colors ${
                                              reminderTime === timeStr ? 'bg-purple-500 text-white' : 'bg-gray-50'
                                            }`}
                                          >
                                            {timeStr}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          ) : (
                            <>
                              {/* Reminder Column - Button only */}
                              <td className="px-1 py-2">
                                <button
                                  onClick={() => {
                                    setActiveReminderItem(item.id);
                                    setShowDatePicker(false);
                                    setShowTimePicker(false);
                                    // Pre-fill if there's an existing reminder
                                    if (itemReminder) {
                                      setReminderDate(itemReminder.reminder_date);
                                      setReminderTime(itemReminder.reminder_time);
                                      setReminderNote(itemReminder.reminder_note);
                                    } else {
                                      setReminderDate('');
                                      setReminderTime('08:00');
                                      setReminderNote('');
                                    }
                                  }}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                    item.has_reminder || itemReminder
                                      ? 'bg-purple-500 text-white'
                                      : 'bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500'
                                  }`}
                                  title={item.has_reminder ? 'View/Edit Reminder' : 'Add Reminder'}
                                >
                                  <Bell className="h-4 w-4" />
                                </button>
                              </td>
                              <td className="px-3 py-2 text-right text-sm font-medium">{item.stock_value?.toFixed(0) || '0'}</td>
                              <td className="px-3 py-2 text-right text-sm">{item.stock_quantity || '0'}</td>
                              <td className="px-3 py-2 text-center text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                  (item.stock_days || '').toLowerCase().includes('no sale') ? 'bg-red-100 text-red-700' :
                                  (item.stock_days || '').includes('50') ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {item.stock_days || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  {/* Critical - Red */}
                                  <button
                                    onClick={() => handleWeeklyStatusChange(item.id, item.status === 'critical' ? '' : 'critical')}
                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                                      item.status === 'critical' 
                                        ? 'bg-red-500 border-red-500 text-white' 
                                        : 'border-red-300 text-red-400 hover:bg-red-100'
                                    }`}
                                    title="Critical"
                                  >
                                    C
                                  </button>
                                  {/* Need to Monitor - Yellow */}
                                  <button
                                    onClick={() => handleWeeklyStatusChange(item.id, item.status === 'monitor' ? '' : 'monitor')}
                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                                      item.status === 'monitor' 
                                        ? 'bg-yellow-500 border-yellow-500 text-white' 
                                        : 'border-yellow-400 text-yellow-500 hover:bg-yellow-100'
                                    }`}
                                    title="Need to Monitor"
                                  >
                                    M
                                  </button>
                                  {/* OK - Orange */}
                                  <button
                                    onClick={() => handleWeeklyStatusChange(item.id, item.status === 'ok' ? '' : 'ok')}
                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                                      item.status === 'ok' 
                                        ? 'bg-orange-500 border-orange-500 text-white' 
                                        : 'border-orange-400 text-orange-500 hover:bg-orange-100'
                                    }`}
                                    title="OK (Natural Reduction)"
                                  >
                                    O
                                  </button>
                                  {/* Resolved - Green */}
                                  <button
                                    onClick={() => handleWeeklyStatusChange(item.id, item.status === 'resolved' ? '' : 'resolved')}
                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                                      item.status === 'resolved' 
                                        ? 'bg-green-500 border-green-500 text-white' 
                                        : 'border-green-400 text-green-500 hover:bg-green-100'
                                    }`}
                                    title="Resolved"
                                  >
                                    ✓
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="space-y-1">
                                  {/* Previous week's note indicator */}
                                  {item.notes_from_previous && (
                                    <div className={`text-xs px-2 py-0.5 rounded ${
                                      item.notes_updated_from_previous 
                                        ? 'bg-orange-100 text-orange-700' 
                                        : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {item.notes_updated_from_previous ? 'Updated from last week' : "Last week's note"}
                                    </div>
                                  )}
                                  <Textarea
                                    value={item.notes || (item.notes_from_previous && !item.notes_updated_from_previous ? item.previous_week_notes : '') || ''}
                                    onChange={(e) => handleWeeklyNoteChange(item.id, e.target.value)}
                                    placeholder={item.previous_week_notes ? `Previous: ${item.previous_week_notes.substring(0, 30)}...` : 'Add notes...'}
                                    className={`min-h-[45px] text-sm resize-none rounded-lg focus:border-orange-300 ${
                                      item.notes_from_previous && !item.notes_updated_from_previous && !item.notes
                                        ? 'border-amber-300 bg-amber-50' 
                                        : 'border-gray-200'
                                    }`}
                                  />
                                  {/* Reminder line - shown below notes */}
                                  {itemReminder && (
                                    <div className="flex items-center gap-1 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded-lg border border-purple-200">
                                      <Bell className="h-3 w-3 flex-shrink-0" />
                                      <span>({formatReminderDate(itemReminder.reminder_date)}): {itemReminder.reminder_note}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Bar */}
          <div className="p-3 border-t flex items-center justify-between bg-gray-50 rounded-b-2xl">
            <span className="text-sm text-gray-500">
              Showing {((tablePage - 1) * itemsPerPage) + 1} - {Math.min(tablePage * itemsPerPage, filteredWeeklyItems.length)} of {filteredWeeklyItems.length} items
            </span>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTablePage(1)}
                disabled={tablePage === 1}
                className="rounded-full"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                disabled={tablePage === 1}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (tablePage <= 3) {
                    pageNum = i + 1;
                  } else if (tablePage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = tablePage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={tablePage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTablePage(pageNum)}
                      className="rounded-full w-8 h-8 p-0"
                      style={tablePage === pageNum ? { backgroundColor: enconOrange } : {}}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTablePage(p => Math.min(totalPages, p + 1))}
                disabled={tablePage === totalPages}
                className="rounded-full"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTablePage(totalPages)}
                disabled={tablePage === totalPages}
                className="rounded-full"
              >
                Last
              </Button>
            </div>
          </div>
        </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          {loadingWeekly ? 'Loading...' : 'No report selected. Upload a spreadsheet or select a week to view.'}
        </div>
      )}
      
      {/* Weekly Upload Modal */}
      <Dialog open={showWeeklyUploadModal} onOpenChange={setShowWeeklyUploadModal}>
        <DialogContent className="max-w-lg bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-6 h-6" style={{ color: enconOrange }} />
              Upload Weekly Stock Spreadsheet
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingWeekly(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDraggingWeekly(false); }}
              onDrop={(e) => { e.preventDefault(); setIsDraggingWeekly(false); handleWeeklyFileUpload(e.dataTransfer.files[0]); }}
              onClick={() => weeklyFileInputRef.current?.click()}
              className={`border-3 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                isDraggingWeekly ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400'
              }`}
              style={{ borderWidth: '3px' }}
            >
              <div className="flex flex-col items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDraggingWeekly ? 'bg-orange-500' : 'bg-orange-100'}`}>
                  <Upload className={`w-8 h-8 ${isDraggingWeekly ? 'text-white' : 'text-orange-500'}`} />
                </div>
                <p className="text-lg font-semibold text-gray-700">Drag & drop your Excel file here</p>
                <p className="text-sm text-gray-500">or click to browse</p>
                <Button variant="outline" className="rounded-full px-6 border-2" style={{ borderColor: enconOrange, color: enconOrange }}>
                  <FileText className="w-4 h-4 mr-2" />Browse Files
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWeeklyUploadModal(false)} className="rounded-full">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Weekly Summary Modal - Professional Analytics Dashboard */}
      <Dialog open={showWeeklySummaryModal} onOpenChange={setShowWeeklySummaryModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-gradient-to-br from-gray-50 to-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader className="pb-3 border-b">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: enconOrange }}>
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-lg font-bold">Weekly Stock Report Summary</span>
                  {weeklySummaryData?.report && (
                    <p className="text-sm font-normal text-gray-500">
                      W/C {safeFormatDate(weeklySummaryData.report.week_commencing, 'do MMMM yyyy')}
                    </p>
                  )}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {weeklySummaryData && (
            <div className="flex-1 overflow-y-auto space-y-5 pr-1 py-4">
              
              {/* Week-to-Week Variance Bar */}
              {weeklyVarianceData && weeklyVarianceData.variances && weeklyVarianceData.variances.length > 0 && (
                <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-100" data-testid="variance-bar">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Stock Reduction Variance</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {weeklyVarianceData.variances.map((v, idx) => (
                      <div key={idx} className={`text-center px-4 py-2.5 rounded-xl border min-w-[100px] ${
                        v.variance > 0 ? 'bg-green-50 border-green-200' : v.variance < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">
                          Wk{idx + 1} - Wk{idx + 2}
                        </div>
                        <div className={`text-sm font-bold ${
                          v.variance > 0 ? 'text-green-700' : v.variance < 0 ? 'text-red-700' : 'text-gray-500'
                        }`}>
                          {v.variance > 0 ? '+' : ''}£{v.variance.toLocaleString('en-GB', {maximumFractionDigits: 0})}
                        </div>
                      </div>
                    ))}
                    {/* Average variance */}
                    <div className="ml-auto text-center px-4 py-2.5 rounded-xl border-2 min-w-[100px]" style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}>
                      <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: enconOrange }}>Avg Variance</div>
                      <div className="text-sm font-bold" style={{ color: enconOrange }}>
                        {weeklyVarianceData.average_variance > 0 ? '+' : ''}£{weeklyVarianceData.average_variance.toLocaleString('en-GB', {maximumFractionDigits: 0})}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Row 1: Performance KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* This Week - Resolved */}
                <div className="relative bg-white rounded-2xl p-5 shadow-md border-2 overflow-hidden" style={{ borderColor: '#ee7203' }}>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-green-100 to-transparent rounded-bl-full"></div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">This Week</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      {weeklySummaryData.summary.percentage_reduced_this_week?.toFixed(1) || '0.0'}%
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      £{(weeklySummaryData.summary.value_resolved || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{weeklySummaryData.summary.resolved_count || 0} lines resolved</div>
                  </div>
                </div>
                
                {/* Overall - Cumulative */}
                <div className="relative bg-white rounded-2xl p-5 shadow-md border-2 overflow-hidden" style={{ borderColor: '#ee7203' }}>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-100 to-transparent rounded-bl-full"></div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Target className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Overall</span>
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mb-1">
                      {weeklySummaryData.summary.percentage_reduced_overall?.toFixed(1) || '0.0'}%
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      £{(weeklySummaryData.summary.total_resolved_value_overall || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{weeklySummaryData.summary.total_resolved_items_overall || 0} items cumulative</div>
                  </div>
                </div>
                
                {/* Total Stock to Reduce */}
                <div className="relative bg-white rounded-2xl p-5 shadow-md border-2 overflow-hidden" style={{ borderColor: '#ee7203', backgroundColor: 'rgb(250, 245, 255)' }}>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-200 to-transparent rounded-bl-full"></div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-200 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-purple-700" />
                      </div>
                      <span className="text-xs text-purple-700 uppercase tracking-wider font-semibold">51+ Days Stock</span>
                    </div>
                    <div className="text-3xl font-bold text-purple-700 mb-1">
                      £{(weeklySummaryData.summary.value_51_plus || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}
                    </div>
                    <div className="text-xs text-purple-600 mt-1">Target stock to reduce</div>
                    <div className="text-sm font-medium text-purple-700 mt-1">
                      {weeklySummaryData.summary.entries_51_plus_days || 0} items
                    </div>
                  </div>
                </div>
                
                {/* Total Value */}
                <div className="relative bg-white rounded-2xl p-5 shadow-md border-2 overflow-hidden" style={{ borderColor: enconOrange }}>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-orange-100 to-transparent rounded-bl-full"></div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(255, 237, 213)' }}>
                        <Package className="w-4 h-4" style={{ color: enconOrange }} />
                      </div>
                      <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: enconOrange }}>Total Value</span>
                    </div>
                    <div className="text-3xl font-bold" style={{ color: enconOrange }}>
                      £{(weeklySummaryData.summary.total_value || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Total stock under review</div>
                    <div className="text-sm font-medium text-gray-600 mt-1">
                      {weeklySummaryData.summary.total_entries} lines
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Row 2: Status Breakdown with Circular Progress Style */}
              <div className="grid grid-cols-4 gap-3">
                {/* Critical */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 border border-red-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="relative w-14 h-14">
                      <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-red-200" strokeWidth="3"/>
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-red-500" strokeWidth="3" 
                          strokeDasharray={`${weeklySummaryData.summary.percentage_critical || 0} 100`} strokeLinecap="round"/>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-red-700">{weeklySummaryData.summary.percentage_critical?.toFixed(0) || 0}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-red-600 uppercase tracking-wide font-semibold">Critical</div>
                      <div className="text-2xl font-bold text-red-700">{weeklySummaryData.summary.critical_count || 0}</div>
                      <div className="text-xs text-red-600">£{(weeklySummaryData.summary.value_critical || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</div>
                    </div>
                  </div>
                </div>
                
                {/* Need to Monitor */}
                <div className="bg-gradient-to-br from-yellow-50 to-amber-100 rounded-2xl p-4 border border-yellow-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="relative w-14 h-14">
                      <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-yellow-200" strokeWidth="3"/>
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-yellow-500" strokeWidth="3" 
                          strokeDasharray={`${weeklySummaryData.summary.percentage_monitor || 0} 100`} strokeLinecap="round"/>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-yellow-700">{weeklySummaryData.summary.percentage_monitor?.toFixed(0) || 0}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-amber-600 uppercase tracking-wide font-semibold">Need to Monitor</div>
                      <div className="text-2xl font-bold text-amber-700">{weeklySummaryData.summary.monitor_count || 0}</div>
                      <div className="text-xs text-amber-600">£{(weeklySummaryData.summary.value_monitor || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</div>
                    </div>
                  </div>
                </div>
                
                {/* OK - Natural Reduction */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4 border border-orange-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="relative w-14 h-14">
                      <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-orange-200" strokeWidth="3"/>
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-orange-500" strokeWidth="3" 
                          strokeDasharray={`${weeklySummaryData.summary.percentage_ok || 0} 100`} strokeLinecap="round"/>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-orange-700">{weeklySummaryData.summary.percentage_ok?.toFixed(0) || 0}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-orange-600 uppercase tracking-wide font-semibold">OK (No Concern)</div>
                      <div className="text-2xl font-bold text-orange-700">{weeklySummaryData.summary.ok_count || 0}</div>
                      <div className="text-xs text-orange-600">£{(weeklySummaryData.summary.value_ok || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</div>
                    </div>
                  </div>
                </div>
                
                {/* Products with Notes */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-4 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="relative w-14 h-14">
                      <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-blue-200" strokeWidth="3"/>
                        <circle cx="18" cy="18" r="15" fill="none" className="stroke-blue-500" strokeWidth="3" 
                          strokeDasharray={`${weeklySummaryData.summary.percentage_with_notes || 0} 100`} strokeLinecap="round"/>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-700">{weeklySummaryData.summary.percentage_with_notes?.toFixed(0) || 0}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-blue-600 uppercase tracking-wide font-semibold">With Notes</div>
                      <div className="text-2xl font-bold text-blue-700">{weeklySummaryData.summary.entries_with_notes || 0}</div>
                      <div className="text-xs text-blue-600">£{(weeklySummaryData.summary.value_with_notes || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats Row */}
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[100px] bg-white rounded-xl p-3 border-2 shadow-sm text-center" style={{ borderColor: '#ee7203' }}>
                  <div className="text-2xl font-bold text-gray-800">{weeklySummaryData.summary.total_entries}</div>
                  <div className="text-xs text-gray-500 uppercase">Total Lines</div>
                </div>
                <div className="flex-1 min-w-[100px] bg-white rounded-xl p-3 border-2 shadow-sm text-center" style={{ borderColor: '#ee7203' }}>
                  <div className="text-2xl font-bold text-orange-600">{weeklySummaryData.summary.entries_50_plus_days}</div>
                  <div className="text-xs text-gray-500 uppercase">50+ Days</div>
                </div>
                <div className="flex-1 min-w-[100px] bg-green-50 rounded-xl p-3 border-2 shadow-sm text-center" style={{ borderColor: '#ee7203' }}>
                  <div className="text-2xl font-bold text-green-700">{weeklySummaryData.summary.resolved_count || 0}</div>
                  <div className="text-xs text-green-600 uppercase">Resolved</div>
                </div>
                <div className="flex-1 min-w-[100px] bg-gray-50 rounded-xl p-3 border-2 shadow-sm text-center" style={{ borderColor: '#ee7203' }}>
                  <div className="text-2xl font-bold text-gray-500">{(weeklySummaryData.summary.total_entries || 0) - (weeklySummaryData.summary.entries_with_notes || 0)}</div>
                  <div className="text-xs text-gray-500 uppercase">No Notes</div>
                </div>
              </div>
              
              {/* Items Table */}
              <div className="border rounded-2xl overflow-hidden shadow-sm bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white" style={{ backgroundColor: enconOrange }}>
                      <th className="px-3 py-3 text-left font-medium">Code</th>
                      <th className="px-3 py-3 text-left font-medium">Description</th>
                      <th className="px-3 py-3 text-right font-medium">Value</th>
                      <th className="px-3 py-3 text-center font-medium">Status</th>
                      <th className="px-3 py-3 text-left font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const itemsArray = weeklySummaryData.items || [];
                      const itemsWithNotesOrStatus = itemsArray.filter(i => (i.notes || '').trim() || i.status);
                      const itemsWithoutNotes = itemsArray.filter(i => !(i.notes || '').trim() && !i.status);
                      
                      const sortedWithNotes = [...itemsWithNotesOrStatus].sort((a, b) => {
                        const statusOrder = { 'critical': 0, 'monitor': 1, 'ok': 2, 'resolved': 3 };
                        const aOrder = statusOrder[a.status] ?? 4;
                        const bOrder = statusOrder[b.status] ?? 4;
                        return aOrder - bOrder;
                      });
                      
                      const allSortedItems = [...sortedWithNotes, ...itemsWithoutNotes];
                      
                      return allSortedItems.map(item => (
                        <tr key={item.id} className={`border-b transition-colors ${
                          item.status === 'critical' ? 'bg-red-50 hover:bg-red-100' : 
                          item.status === 'monitor' ? 'bg-yellow-50 hover:bg-yellow-100' : 
                          item.status === 'ok' ? 'bg-orange-50 hover:bg-orange-100' : 
                          item.status === 'resolved' ? 'bg-green-50 hover:bg-green-100' : 
                          (item.notes || '').trim() ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-50 text-gray-400'
                        }`}>
                          <td className="px-3 py-2 font-mono text-xs">{item.product_code}</td>
                          <td className="px-3 py-2 text-sm">{item.product_description?.substring(0, 40)}</td>
                          <td className="px-3 py-2 text-right font-medium">£{(item.stock_value || 0).toLocaleString('en-GB', {maximumFractionDigits: 0})}</td>
                          <td className="px-3 py-2 text-center">
                            {item.status === 'critical' && <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-medium">Critical</span>}
                            {item.status === 'monitor' && <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-medium">Monitor</span>}
                            {item.status === 'ok' && <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded-full text-xs font-medium">OK</span>}
                            {item.status === 'resolved' && <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-xs font-medium">Resolved</span>}
                            {!item.status && <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-sm">{item.notes?.substring(0, 50) || '-'}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-4 flex justify-between items-center">
            <div className="text-xs text-gray-500 italic">
              Report Purpose: Reduce stock value on items with 51+ days of supply
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowWeeklySummaryModal(false)} className="rounded-full">Close</Button>
              <Button onClick={downloadWeeklyPDF} className="rounded-full gap-2" style={{ backgroundColor: enconOrange }}>
                <Download className="w-4 h-4" />Download PDF
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  // ================== MAIN RENDER ==================
  // ── Derived: is today's Daily Stock Profile (BI report) uploaded? ──────
  const todayStr = new Date().toISOString().slice(0, 10);
  const profileUploadedToday = sosLatestUploadDate && sosLatestUploadDate.slice(0, 10) === todayStr;
  // "Missing" when either no upload ever exists (sosNoDaily=true), or the
  // latest upload is from a previous day — same condition previously used to
  // decide whether to force the blocking modal, now used by the Upload
  // Reports bar instead.
  const dailyProfileMissing = currentView === 'stock-ordering' && (
    sosNoDaily === true ||
    (sosLatestUploadDate !== null && !profileUploadedToday)
  );



  // ── Global sub-header content (moved out of the old in-page orange menu bar) ──
  const smSubHeaderLeft = (
    <div className="relative" ref={menuDropdownRef}>
      <button
        onClick={() => setMenuDropdownOpen(o => !o)}
        className={`h-7 flex items-center gap-1.5 px-3 rounded-xl border-2 border-white whitespace-nowrap hover:shadow-lg transition-all duration-200 hover:scale-105 ${menuDropdownOpen ? 'shadow-lg' : ''}`}
        style={{ background: 'linear-gradient(135deg, #ee7203 0%, #f4a35f 100%)' }}
        data-testid="stock-menu-dropdown-btn"
      >
        {currentView === 'stock-ordering' && <><ShoppingCart className="w-3.5 h-3.5 text-white" /> <span className="hidden sm:inline text-white font-semibold text-xs">Stock Ordering</span></>}
        {currentView === 'stocktake' && <><ClipboardList className="w-3.5 h-3.5 text-white" /> <span className="hidden sm:inline text-white font-semibold text-xs">Stocktake</span></>}
        {currentView === 'daily-overstock' && <><ShoppingCart className="w-3.5 h-3.5 text-white" /> <span className="hidden sm:inline text-white font-semibold text-xs">Daily Purchasing</span></>}
        {currentView === 'weekly-stock' && <><BarChart3 className="w-3.5 h-3.5 text-white" /> <span className="hidden sm:inline text-white font-semibold text-xs">Weekly Stock</span></>}
        {currentView === 'stock-efficiency' && <><TrendingUp className="w-3.5 h-3.5 text-white" /> <span className="hidden sm:inline text-white font-semibold text-xs">Stock Efficiency</span></>}
        <ChevronDown className={`w-3.5 h-3.5 text-white transition-transform duration-200 ${menuDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} />
      </button>

      {menuDropdownOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuDropdownOpen(false)} />
          <div
            className="absolute left-1/2 -translate-x-1/2 mt-1 w-52 rounded-2xl shadow-xl overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
              border: '2px solid #ee7203',
              top: '100%',
              zIndex: 9999,
            }}
          >
            {[
              { id: 'stock-ordering', icon: <ShoppingCart className="w-4 h-4" />, label: 'Stock Ordering' },
              { id: 'stock-efficiency', icon: <TrendingUp className="w-4 h-4" />, label: 'Stock Efficiency' },
              { id: 'stocktake', icon: <ClipboardList className="w-4 h-4" />, label: 'Stocktake' },
              { id: 'daily-overstock', icon: <ShoppingCart className="w-4 h-4" />, label: 'Daily Purchasing' },
              { id: 'weekly-stock', icon: <BarChart3 className="w-4 h-4" />, label: 'Weekly Stock Report' },
            ].map((opt, idx) => (
              <button
                key={opt.id}
                onClick={() => { setCurrentView(opt.id); setMenuDropdownOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors border-t border-white/10 ${idx === 0 ? 'rounded-t-xl border-t-0' : ''} ${idx === 4 ? 'rounded-b-xl' : ''} ${
                  currentView === opt.id ? 'font-semibold bg-white/15' : 'text-white hover:bg-white/10'
                }`}
                style={currentView === opt.id ? { color: '#f4a35f' } : {}}
                data-testid={`menu-opt-${opt.id}`}
              >
                {opt.icon}
                <span className="font-medium">{opt.label}</span>
                {currentView === opt.id && <CheckCircle className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const smSubHeaderCenter = null;

  const smSubHeaderRight = (
    <div className="flex items-center gap-1.5">
      {currentView === 'stock-efficiency' && (
        <div className="relative">
          <button
            ref={efficiencyDateBtnRef}
            type="button"
            onClick={() => setEfficiencyDatesOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/90 hover:bg-white transition-colors"
            style={{ color: efficiencyOldDate ? '#b45309' : enconOrange }}
            data-testid="stock-efficiency-change-date-btn"
          >
            <Calendar className="w-3 h-3" />
            {efficiencyOldDate ? format(parseISO(efficiencyOldDate), 'dd MMM') : 'Change Date'}
          </button>
          {efficiencyDatesOpen && (
            <div
              ref={efficiencyDatePopoverRef}
              className="absolute right-0 top-full mt-2 z-[9999] bg-white border-2 rounded-xl shadow-2xl p-2"
              style={{ borderColor: enconOrange }}
              data-testid="stock-efficiency-change-date-popover"
            >
              <CalendarPicker
                mode="single"
                selected={efficiencyOldDate ? parseISO(efficiencyOldDate) : undefined}
                onSelect={(date) => {
                  if (date) setEfficiencyOldDate(format(date, 'yyyy-MM-dd'));
                  setEfficiencyDatesOpen(false);
                }}
                disabled={(date) => {
                  const d = format(date, 'yyyy-MM-dd');
                  return d > format(new Date(), 'yyyy-MM-dd') || (efficiencyAvailableDates.length > 0 && !efficiencyAvailableDates.includes(d));
                }}
                className="rounded-lg"
              />
              {efficiencyOldDate && (
                <button
                  type="button"
                  onClick={() => { setEfficiencyOldDate(null); setEfficiencyDatesOpen(false); }}
                  className="w-full text-center text-xs font-semibold text-gray-500 hover:text-gray-700 py-1.5 mt-1 border-t"
                  data-testid="stock-efficiency-clear-date-btn"
                >
                  Reset to latest
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {currentView === 'stock-efficiency' && (
        <button
          onClick={() => setShowEfficiencyReport(true)}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/90 hover:bg-white transition-colors"
          style={{ color: enconOrange }}
          data-testid="stock-efficiency-view-report-btn"
        >
          <FileText className="w-3 h-3" />
          View Report
        </button>
      )}
      {/* Report status indicator removed — Upload Reports bar (in-page) now
          communicates missing uploads instead of a sub-header pill. */}

      {currentView === 'stock-ordering' && (
        <button
          onClick={() => sosRefreshRef.current?.()}
          className="p-1.5 rounded-full bg-white hover:bg-gray-100 transition-colors"
          title="Refresh"
          data-testid="sos-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${sosUploading ? 'animate-spin' : ''}`} style={{ color: enconOrange }} />
        </button>
      )}

      {currentView === 'stocktake' && (
        <div className="relative" ref={stockTakeDropdownRef}>
          <button
            onClick={() => setStockTakeDropdownOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white hover:bg-orange-50 transition-all max-w-[160px]"
            style={{ color: enconOrange }}
            data-testid="stocktake-session-btn"
          >
            <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{activeST?.name || 'Stock Take'}</span>
            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${stockTakeDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {stockTakeDropdownOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setStockTakeDropdownOpen(false)} />
              <div className="absolute right-0 mt-1 w-56 rounded-xl shadow-xl border bg-white z-50 overflow-hidden">
                <p className="px-3 py-2 text-xs font-bold text-gray-400 border-b">Stock Takes</p>
                {stockTakes.map(st => (
                  <button
                    key={st.id}
                    onClick={() => { setActiveStockTakeId(st.id); setStockTakeDropdownOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${st.id === activeStockTakeId ? 'bg-orange-50 font-semibold' : 'hover:bg-gray-50'}`}
                    style={st.id === activeStockTakeId ? { color: enconOrange } : {}}
                  >
                    <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{st.name}</span>
                  </button>
                ))}
                <div className="border-t border-gray-100">
                  <button
                    onClick={() => { setShowNewStocktakeDialog(true); setStockTakeDropdownOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-orange-600 hover:bg-orange-50 transition-colors"
                    data-testid="add-new-stocktake-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add New
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader user={user} subHeaderLeft={smSubHeaderLeft} subHeaderCenter={smSubHeaderCenter} subHeaderRight={smSubHeaderRight} />

      {/* PDF Download Progress Bar */}
      {pdfDownloading && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-white shadow-lg border-b-2" style={{ borderColor: enconOrange }}>
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: enconOrange }} />
                <span className="font-medium text-gray-700">Generating PDF...</span>
              </div>
              <div className="flex-1">
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${pdfProgress}%`, backgroundColor: enconOrange }}
                  />
                </div>
              </div>
              <span className="text-sm text-gray-500 font-medium">{pdfProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Main content — minimal padding since notification bar is in document flow */}
      <div className={`px-4 lg:px-6 pb-12 pt-[20px] bg-gray-100 min-h-screen`}>
        <div className="max-w-7xl mx-auto">

          {/* ── Upload Reports bar ──────────────────────────── */}
          {currentView === 'stock-ordering' && (dailyProfileMissing || freeStockUploadedToday === false) && (
            <UploadReportsBar
              needFreeStock={freeStockUploadedToday === false}
              needDailyProfile={dailyProfileMissing}
              onFreeStockUploaded={() => {
                setFreeStockUploadedToday(true);
                loadFreeStockStatus();
              }}
              onDailyProfileUploaded={() => {
                setSosLatestUploadDate(new Date().toISOString());
                sosRefreshRef.current?.();
              }}
            />
          )}

          {/* ── Page content ── */}
          {currentView === 'stock-ordering' && <StockOrderingView
            user={user}
            deepLinkRequestUser={deepLinkRequestUser}
            onDailyInfoChange={(info) => {
              setSosLatestUploadDate(info.raw_latest || null);
              setSosNoDaily(!info.has_daily);
            }}
            onRefreshReady={(fn) => { sosRefreshRef.current = fn; }}
            onUploadingChange={(uploading) => setSosUploading(uploading)}
          />}
          {currentView === 'stocktake' && (
            <StocktakeView
              activeStockTakeId={activeStockTakeId}
              allStockTakes={stockTakes}
              onRequestNewStocktake={() => setShowNewStocktakeDialog(true)}
              user={user}
              onDeleted={(deletedId) => {
                setStockTakes(prev => prev.filter(st => st.id !== deletedId));
                setActiveStockTakeId(null);
              }}
            />
          )}
          {currentView === 'daily-overstock' && renderDailyOverstockPage()}
          {currentView === 'weekly-stock' && renderWeeklyStockPage()}
          {currentView === 'stock-efficiency' && (
            <StockEfficiencyView
              user={user}
              showReport={showEfficiencyReport}
              onReportShown={() => setShowEfficiencyReport(false)}
              oldDate={efficiencyOldDate}
            />
          )}

        </div>
      </div>

      {/* ── New Stock Take dialog (2-step) ──────────────────────────────────── */}
      {showNewStocktakeDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onDragOver={e => { e.preventDefault(); setStDragOver(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setStDragOver(false); }}
          onDrop={e => {
            e.preventDefault();
            setStDragOver(false);
            if (newStocktakeStep === 2) {
              const f = e.dataTransfer.files[0];
              if (f) handleStocktakeFileSelected(f);
            }
          }}
        >
          {/* Full-screen drop highlight */}
          {stDragOver && newStocktakeStep === 2 && (
            <div className="absolute inset-0 border-4 border-dashed rounded-none pointer-events-none z-10 flex items-center justify-center"
              style={{ borderColor: enconOrange, backgroundColor: 'rgba(238,114,3,0.08)' }}>
              <div className="text-center">
                <Upload className="w-16 h-16 mx-auto mb-3" style={{ color: enconOrange }} />
                <p className="text-2xl font-bold" style={{ color: enconOrange }}>Drop Free Stock Report here</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden relative z-20">
            {/* Step 1 — Pick date */}
            {newStocktakeStep === 1 && (
              <>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">New Stock Take — Step 1 of 2</h3>
                  <p className="text-sm text-gray-500 mb-5">Select the date of your stock take.</p>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Stock Take Date</label>
                  <input
                    type="date"
                    value={newStocktakeDate}
                    onChange={e => setNewStocktakeDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 outline-none"
                    data-testid="new-stocktake-date"
                    autoFocus
                  />
                  {newStocktakeDate && (
                    <p className="mt-2 text-xs text-gray-400">
                      Name preview:{' '}
                      <span className="font-semibold text-gray-600">
                        {`Stocktake ${new Date(newStocktakeDate).toLocaleString('en-GB', { month: 'long', year: 'numeric' })}`}
                      </span>
                    </p>
                  )}
                </div>
                <div className="border-t border-gray-100 flex">
                  <button onClick={closeNewStocktakeDialog} className="flex-1 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                  <div className="w-px bg-gray-100" />
                  <button
                    onClick={() => { if (!newStocktakeDate) { toast.error('Please select a date'); return; } setNewStocktakeStep(2); }}
                    disabled={!newStocktakeDate}
                    className="flex-1 py-3 text-sm font-bold text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{ backgroundColor: enconOrange }}
                    data-testid="stocktake-next-btn"
                  >
                    Next — Upload Report
                  </button>
                </div>
              </>
            )}

            {/* Step 2 — Upload free stock report */}
            {newStocktakeStep === 2 && (
              <>
                <div className="p-6">
                  <button
                    onClick={() => setNewStocktakeStep(1)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mb-4 transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                  </button>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Upload Free Stock Report</h3>
                  <p className="text-sm text-gray-500 mb-5">
                    Drag the GSTVOOO CSV anywhere on screen, drop it in the box below, or click Browse.
                  </p>

                  {creatingST ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                      <Loader2 className="w-10 h-10 animate-spin" style={{ color: enconOrange }} />
                      <p className="text-sm font-semibold text-gray-600">Uploading report and creating stock take…</p>
                    </div>
                  ) : (
                    <div
                      className="rounded-xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center py-10 gap-3 select-none"
                      style={{
                        borderColor: stDragOver ? enconOrange : '#d1d5db',
                        backgroundColor: stDragOver ? 'rgba(238,114,3,0.04)' : '#fafafa',
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setStDragOver(true); }}
                      onDragLeave={e => { e.stopPropagation(); setStDragOver(false); }}
                      onDrop={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStDragOver(false);
                        const f = e.dataTransfer.files[0];
                        if (f) handleStocktakeFileSelected(f);
                      }}
                      onClick={() => stFileInputRef.current?.click()}
                      data-testid="stocktake-drop-zone"
                    >
                      <Upload className="w-10 h-10" style={{ color: enconOrange }} />
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-700">Drag &amp; drop anywhere on screen</p>
                        <p className="text-xs text-gray-400 mt-0.5">or click here to browse</p>
                        <p className="text-xs text-gray-400 mt-1">Accepts .csv, .xlsx, .xls</p>
                      </div>
                    </div>
                  )}

                  <input
                    ref={stFileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    data-testid="stocktake-file-input"
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) handleStocktakeFileSelected(f);
                    }}
                  />
                </div>
                {!creatingST && (
                  <div className="border-t border-gray-100">
                    <button onClick={closeNewStocktakeDialog} className="w-full py-3 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancelled Ticket flagging moved to the global Admin dropdown (Cancelled Notif) */}
    </div>
  );
};

export default StockManagement;
